<template>
  <div class="pos-view" @keydown="handleKeydown">
    <!-- Hidden barcode input for hardware scanners -->
    <input
      ref="barcodeInputRef"
      type="text"
      class="pos-barcode-input"
      @input="handleBarcodeInput"
      @keydown.enter="processBarcodeBuffer"
    />

    <!-- POS Header -->
    <header class="pos-header">
      <div class="pos-header-left">
        <!-- Back Button -->
        <button class="pos-header-btn" tabindex="-1" @click="goBack" title="Back to Dashboard">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <!-- Hamburger Menu -->
        <button class="pos-header-btn" tabindex="-1" @click="showNavDrawer = true" title="Menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div class="pos-logo">
          <span class="pos-logo-text">{{ businessName || 'OnePlaceHQ' }}</span>
          <span class="pos-logo-product">POS</span>
        </div>
      </div>
      <div class="pos-header-center">
        <span class="pos-time">{{ currentTime }}</span>
      </div>
      <div class="pos-header-right">
        <span class="pos-user">{{ user?.username || 'User' }}</span>
        <button class="pos-header-btn" tabindex="-1" @click="handleLogout" title="Logout">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </header>

    <!-- Main POS Layout -->
    <div class="pos-main">
      <!-- Left Panel: Cart -->
      <div class="pos-cart-panel">
        <!-- Customer: unified search-or-create (HSN-style) -->
        <div class="pos-customer-bar">
          <div class="pos-customer-search-wrapper">
            <svg class="pos-customer-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            </svg>
            <input
              ref="customerSearchRef"
              :value="customerSearchQuery"
              @input="onCustomerSearchInput"
              type="text"
              class="pos-customer-search-input"
              :placeholder="selectedCustomer ? '' : 'F3 — Phone (10 digits) or name. Enter for walk-in'"
              @focus="customerDropdownOpen = customerSearchResults.length > 0 || customerSearchQuery.length >= 2"
              @blur="closeCustomerDropdown"
              @keydown="handleCustomerKeydown"
            />
            <!-- Selected customer badge -->
            <div v-if="selectedCustomer && !customerSearchQuery" class="pos-customer-badge" @click="customerSearchRef?.focus()">
              <span class="pos-customer-badge-name">{{ selectedCustomer.name }}</span>
              <span v-if="selectedCustomer.phone" class="pos-customer-badge-phone">{{ selectedCustomer.phone }}</span>
              <span v-if="selectedCustomer.loyalty_points_balance" class="pos-customer-badge-pts">{{ Math.floor(selectedCustomer.loyalty_points_balance) }} pts</span>
              <button class="pos-customer-badge-clear" tabindex="-1" @mousedown.prevent="clearCustomerSelection">x</button>
            </div>
            <!-- Dropdown -->
            <div v-if="customerDropdownOpen" class="pos-customer-dropdown-list">
              <div v-if="isLoadingCustomers" class="pos-customer-dropdown-loading">Searching...</div>
              <template v-else>
                <div
                  v-for="(cust, ci) in customerSearchResults"
                  :key="cust.id"
                  class="pos-customer-dropdown-item"
                  :class="{ highlighted: customerHighlight === ci }"
                  @mousedown.prevent="selectCustomerResult(cust)"
                >
                  <span class="pos-customer-item-name">{{ cust.name }}</span>
                  <span class="pos-customer-item-phone">{{ cust.phone || '' }}</span>
                </div>
                <!-- "Create new" option at bottom -->
                <div
                  v-if="customerSearchQuery.length >= 2"
                  class="pos-customer-dropdown-item pos-customer-create-option"
                  :class="{ highlighted: customerHighlight === customerSearchResults.length }"
                  @mousedown.prevent="startInlineCreate"
                >
                  <span>+ New customer {{ isDigitsOnly(customerSearchQuery) ? customerSearchQuery : '"' + customerSearchQuery + '"' }} [Enter]</span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Inline create (appears when "Create" selected from dropdown) -->
        <div v-if="showNewCustomerForm" class="pos-new-customer-inline" @keydown="handleNewCustomerFormKeydown">
          <!-- Phone-first: phone pre-filled, need name -->
          <template v-if="customerCreateMode === 'phone-first'">
            <span class="pos-new-customer-inline-label">{{ newCustomer.phone }}</span>
            <input
              ref="newCustomerNameRef"
              v-model="newCustomer.name"
              type="text"
              class="pos-new-customer-inline-input"
              placeholder="Customer name..."
              @keydown.enter.prevent="createAndSelectCustomer"
              @keydown.escape.prevent="cancelNewCustomer"
            />
          </template>
          <!-- Name-first: name pre-filled, need phone -->
          <template v-else>
            <span class="pos-new-customer-inline-label">{{ newCustomer.name }}</span>
            <IntercomPhoneInput
              ref="newCustomerPhoneRef"
              v-model="newCustomer.phone"
              :placeholder="$t('pos.customerPhone')"
              :error="phoneError"
              @update:valid="(v) => (posPhoneValid = v)"
            />
          </template>
          <div class="pos-new-customer-inline-actions">
            <button class="pos-new-customer-inline-cancel" @click="cancelNewCustomer">Esc</button>
            <IntercomButton
              variant="primary"
              size="sm"
              :loading="isCreatingCustomer"
              :disabled="!newCustomer.name || (newCustomer.phone && !posPhoneValid)"
              @click="createAndSelectCustomer"
            >
              Create [Enter]
            </IntercomButton>
          </div>
        </div>

        <!-- Product Search (inline above cart) -->
        <div class="pos-search-bar" :class="{ 'has-dropdown': searchQuery && searchDropdownOpen }">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="pos-search-input"
            :placeholder="$t('pos.searchPlaceholder')"
            @input="handleSearch"
            @keydown="handleSearchKeydown"
            @focus="searchDropdownOpen = searchQuery.length > 0"
            @blur="closeSearchDropdown"
          />
          <button v-if="searchQuery" class="pos-search-clear" tabindex="-1" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button class="pos-camera-btn" tabindex="-1" @click="showCameraScanner = true" :title="$t('pos.scanBarcode')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
          <!-- Search Dropdown -->
          <div v-if="searchQuery && searchDropdownOpen" class="pos-search-dropdown">
            <div
              v-for="(product, pi) in filteredProducts.slice(0, 10)"
              :key="product.id"
              class="pos-search-dropdown-item"
              :class="{ highlighted: searchHighlight === pi, 'out-of-stock': product.stock_quantity <= 0 }"
              @mousedown.prevent="addProductFromSearch(product)"
            >
              <div class="pos-search-item-info">
                <span class="pos-search-item-name">{{ product.name }}</span>
                <span class="pos-search-item-sku">{{ product.sku || product.barcode || '' }}</span>
              </div>
              <div class="pos-search-item-right">
                <span class="pos-search-item-price">Rs {{ formatPrice(product.base_price) }}</span>
                <span class="pos-search-item-stock" :class="{ low: product.stock_quantity < 10 }">{{ product.stock_quantity }} in stock</span>
              </div>
            </div>
            <div v-if="filteredProducts.length === 0" class="pos-search-dropdown-empty">
              No products found
            </div>
          </div>
        </div>

        <!-- Cart Header (column labels) -->
        <div v-if="hasItems" class="pos-cart-header">
          <div class="pos-col pos-col-name">Product</div>
          <div class="pos-col pos-col-qty">Qty</div>
          <div class="pos-col pos-col-price">Price</div>
          <div class="pos-col pos-col-disc">Disc</div>
          <div class="pos-col pos-col-tax">Tax</div>
          <div class="pos-col pos-col-total">Total</div>
          <div class="pos-col pos-col-del"></div>
        </div>

        <!-- Cart Items -->
        <div class="pos-cart-items" ref="cartItemsRef">
          <div v-if="!hasItems" class="pos-cart-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <p>{{ $t('pos.emptyCart') }}</p>
            <span>{{ $t('pos.scanOrSearch') }}</span>
          </div>

          <div
            v-for="(item, index) in cartItems"
            :key="item.product_id"
            class="pos-cart-item"
            :class="{ selected: selectedCartItemIndex === index }"
            :tabindex="index === 0 ? 0 : -1"
            @click="selectCartItem(index); cartItemSubFocus = -1"
            @focus="selectCartItem(index)"
            @keydown="handleCartItemKeydown($event, index)"
          >
            <!-- Product name -->
            <div class="pos-col pos-col-name" :title="item.product_name">
              <span class="pos-cart-item-name">{{ item.product_name }}</span>
              <span v-if="item.sku" class="pos-cart-item-sku">{{ item.sku }}</span>
            </div>
            <!-- Qty: -/+/input -->
            <div class="pos-col pos-col-qty">
              <div class="pos-qty-group">
                <button class="pos-qty-btn" tabindex="-1" @click.stop="decrementQuantity(index)">-</button>
                <input
                  :value="item.quantity"
                  type="number"
                  class="pos-field-input pos-field-input--qty"
                  :data-field="0"
                  min="0"
                  :step="getQtyStep(item.unit_type)"
                  tabindex="-1"
                  @change="handleQtyInputChange(index, $event)"
                  @click.stop
                  @keydown.stop="handleFieldKeydown($event, index, 0)"
                />
                <button class="pos-qty-btn" tabindex="-1" @click.stop="handleIncrement(index)">+</button>
              </div>
              <span v-if="item.unit_type" class="pos-unit-label">{{ item.unit_type }}</span>
            </div>
            <!-- Price -->
            <div class="pos-col pos-col-price">
              <input
                :value="item.unit_price"
                type="number"
                class="pos-field-input"
                :data-field="1"
                min="0"
                step="0.01"
                tabindex="-1"
                @change="handlePriceInputChange(index, $event)"
                @click.stop
                @keydown.stop="handleFieldKeydown($event, index, 1)"
              />
            </div>
            <!-- Discount -->
            <div class="pos-col pos-col-disc">
              <div class="pos-disc-group">
                <input
                  :value="item.discount_value || ''"
                  type="number"
                  class="pos-field-input pos-field-input--disc"
                  :data-field="2"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  tabindex="-1"
                  @change="handleItemDiscountChange(index, $event)"
                  @click.stop
                  @keydown.stop="handleFieldKeydown($event, index, 2)"
                />
                <button
                  type="button"
                  class="pos-disc-toggle"
                  tabindex="-1"
                  @click.stop="toggleItemDiscountType(index)"
                >{{ item.discount_type === 'percentage' ? '%' : '₹' }}</button>
              </div>
            </div>
            <!-- Tax (read-only) -->
            <div class="pos-col pos-col-tax">
              <span class="pos-tax-display">{{ formatTaxRate(item) || '--' }}</span>
            </div>
            <!-- Total (computed) -->
            <div class="pos-col pos-col-total">
              <span class="pos-total-display">₹{{ formatPrice(lineTotal(item)) }}</span>
            </div>
            <!-- Delete -->
            <div class="pos-col pos-col-del">
              <button class="pos-cart-item-remove" tabindex="-1" @click.stop="removeItem(index)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Cart Totals -->
        <div class="pos-cart-totals">
          <div class="pos-total-row">
            <span>{{ $t('pos.subtotal') }}</span>
            <span>₹{{ formatPrice(taxableSubtotal) }}</span>
          </div>
          <div v-if="taxBreakdown.cgst > 0" class="pos-total-row pos-total-tax">
            <span>CGST</span>
            <span>₹{{ formatPrice(taxBreakdown.cgst) }}</span>
          </div>
          <div v-if="taxBreakdown.sgst > 0" class="pos-total-row pos-total-tax">
            <span>SGST</span>
            <span>₹{{ formatPrice(taxBreakdown.sgst) }}</span>
          </div>
          <div v-if="taxBreakdown.igst > 0" class="pos-total-row pos-total-tax">
            <span>IGST</span>
            <span>₹{{ formatPrice(taxBreakdown.igst) }}</span>
          </div>
          <div v-if="itemDiscountTotal > 0" class="pos-total-row pos-total-discount">
            <span>Item Discounts</span>
            <span>-₹{{ formatPrice(itemDiscountTotal) }}</span>
          </div>
          <div v-if="invoiceDiscount > 0" class="pos-total-row pos-total-discount">
            <span>{{ $t('pos.discount') }}</span>
            <span>-₹{{ formatPrice(invoiceDiscount) }}</span>
          </div>
          <div v-if="loyaltyDiscountAmount > 0" class="pos-total-row pos-total-discount">
            <span>{{ $t('pos.loyaltyDiscount') }}</span>
            <span>-₹{{ formatPrice(loyaltyDiscountAmount) }}</span>
          </div>
          <div class="pos-total-row pos-total-grand">
            <span>{{ $t('pos.grandTotal') }}</span>
            <span>₹{{ formatPrice(grandTotal) }}</span>
          </div>
        </div>
      </div>

      <!-- Right Panel: Payment only -->
      <div class="pos-right-panel">
        <!-- Payment Panel -->
        <Transition name="slide-right">
          <div v-if="isPaymentPanelOpen" class="pos-payment-panel" @keydown="handlePaymentPanelKeydown">
            <div class="pos-payment-header">
              <button class="pos-back-btn" @click="closePaymentPanel">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>
              <h2>{{ $t('pos.payment') }}</h2>
              <span class="pos-payment-phone">{{ selectedCustomer?.phone || '' }}</span>
            </div>

            <div class="pos-payment-amount">
              <span class="pos-payment-label">{{ $t('pos.amountDue') }}</span>
              <span class="pos-payment-value">₹{{ formatPrice(grandTotal) }}</span>
            </div>

            <!-- Sale Type Toggle: Cash Bill vs Credit (Udhar/Khata) -->
            <div class="pos-sale-type" @keydown="handleSaleTypeKeydown">
              <button
                :class="['sale-type-btn', { active: saleType === 'cash' }]"
                :tabindex="saleType === 'cash' ? 0 : -1"
                @click="saleType = 'cash'"
                ref="saleTypeCashRef"
              >Cash Bill [F2]</button>
              <button
                :class="['sale-type-btn', { active: saleType === 'credit' }]"
                :tabindex="saleType === 'credit' ? 0 : -1"
                @click="saleType = 'credit'"
              >Credit (Udhar) [F2]</button>
            </div>

            <!-- Credit mode: require customer selection -->
            <div v-if="saleType === 'credit' && !selectedCustomer" class="credit-warning">
              Select a customer to save as credit bill
            </div>

            <!-- Credit mode: show customer outstanding balance if available -->
            <div v-if="saleType === 'credit' && selectedCustomer?.outstanding_balance > 0" class="pos-credit-balance">
              Outstanding: ₹{{ formatPrice(selectedCustomer.outstanding_balance) }}
            </div>

            <div class="pos-payment-modes" v-if="saleType === 'cash'">
              <button
                v-for="mode in paymentModes"
                :key="mode.value"
                class="pos-payment-mode"
                :class="{ active: selectedPaymentMode === mode.value }"
                @click="selectedPaymentMode = mode.value"
              >
                <span class="pos-payment-mode-icon">
                  <!-- Cash - Banknotes icon -->
                  <svg v-if="mode.icon === 'cash'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="6" width="20" height="12" rx="2" />
                    <circle cx="12" cy="12" r="3" />
                    <path d="M6 12h.01M18 12h.01" />
                  </svg>
                  <!-- UPI - QR/Phone payment icon -->
                  <svg v-else-if="mode.icon === 'upi'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 17h.01M17 14h.01M20 14h.01M14 20h.01M20 20h.01" />
                  </svg>
                  <!-- Card - Credit card icon -->
                  <svg v-else-if="mode.icon === 'card'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M2 10h20" />
                    <path d="M6 14h4" />
                  </svg>
                  <!-- Bank - Building icon -->
                  <svg v-else-if="mode.icon === 'bank'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                  </svg>
                </span>
                <span class="pos-payment-mode-label">{{ mode.label }}</span>
                <span class="pos-payment-mode-key">[{{ mode.key }}]</span>
              </button>
            </div>

            <!-- GST Toggle (only for GST-registered businesses) -->
            <div v-if="businessTaxRegime === 'gst'" class="pos-gst-toggle">
              <IntercomToggle
                :model-value="applyGST"
                @update:model-value="setApplyGST"
                label="Apply GST to this bill"
              />
              <span v-if="!applyGST" class="pos-gst-hint">Bill will be created as Cash Memo (no tax)</span>
            </div>

            <div v-if="saleType === 'cash'" class="pos-payment-field">
              <IntercomInput
                v-model="amountReceived"
                type="number"
                :label="$t('pos.amountReceived')"
                step="0.01"
                min="0"
                ref="amountInputRef"
              />
              <!-- Quick Cash Denominations -->
              <div v-if="selectedPaymentMode === 'cash'" class="pos-quick-cash">
                <button
                  v-for="denom in quickCashDenominations"
                  :key="denom.amount"
                  class="pos-quick-cash-btn"
                  :class="{ active: Number(amountReceived) === denom.amount }"
                  @click="amountReceived = denom.amount"
                >
                  <span class="pos-quick-cash-key">{{ denom.key }}</span>
                  <span>{{ denom.label === 'Exact' ? 'Exact' : `₹${denom.label}` }}</span>
                </button>
              </div>
            </div>

            <div v-if="saleType === 'cash' && changeDue > 0" class="pos-payment-change">
              <span>{{ $t('pos.changeDue') }}</span>
              <span class="pos-payment-change-value">₹{{ formatPrice(changeDue) }}</span>
            </div>

            <!-- Loyalty Points Redemption -->
            <div v-if="selectedCustomer?.loyalty_points_balance > 0 && loyaltySettings?.is_enabled" class="pos-payment-loyalty">
              <label>{{ $t('pos.redeemPoints') }}</label>
              <div class="pos-loyalty-input">
                <input
                  :value="loyaltyPointsToRedeem"
                  @input="setLoyaltyRedemption(Number($event.target.value) || 0)"
                  type="number"
                  min="0"
                  :max="selectedCustomer.loyalty_points_balance"
                  :placeholder="`Max: ${selectedCustomer.loyalty_points_balance}`"
                />
                <span class="pos-loyalty-value">= ₹{{ formatPrice(loyaltyDiscountAmount) }}</span>
              </div>
              <div class="pos-loyalty-info">
                <span>1 pt = ₹{{ loyaltySettings?.rupees_per_point || 0.10 }}</span>
                <span v-if="loyaltySettings?.min_redeem_points > 0">
                  · Min: {{ loyaltySettings.min_redeem_points }} pts
                </span>
              </div>
            </div>

            <div v-if="saleType === 'cash' && selectedPaymentMode !== 'cash'" class="pos-payment-field">
              <IntercomInput
                v-model="paymentReference"
                type="text"
                :label="$t('pos.referenceNumber')"
                :placeholder="$t('pos.transactionId')"
              />
            </div>

            <IntercomButton
              variant="primary"
              size="lg"
              block
              :loading="isProcessingPayment"
              :disabled="saleType === 'cash' ? Number(amountReceived) < grandTotal : !selectedCustomer"
              :class="['pos-complete-btn', { 'pos-confirm-pulse': saleConfirmPending }]"
              @click="confirmAndCompleteSale"
            >
              {{ saleConfirmPending
                ? (changeDue > 0 ? `Return ₹${formatPrice(changeDue)} — Enter to confirm` : 'Press Enter to confirm')
                : (saleType === 'credit' ? 'Save Credit Bill [Enter]' : $t('pos.completeSale') + ' [Enter]')
              }}
            </IntercomButton>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Qty Multiplier Indicator -->
    <Transition name="fade">
      <div v-if="qtyMultiplier > 1" class="pos-qty-multiplier-badge">
        {{ qtyMultiplier }}x
      </div>
    </Transition>

    <!-- Action Bar -->
    <footer class="pos-action-bar" @keydown="handleActionBarKeydown">
      <div class="pos-action-left">
        <button
          class="pos-action-btn"
          :tabindex="actionBarFocusIndex === 0 ? 0 : -1"
          data-action-index="0"
          @click="customerSearchRef?.focus()"
          @focus="actionBarFocusIndex = 0"
        >
          <span class="pos-action-key">F3</span>
          <span>{{ $t('pos.customer') }}</span>
        </button>
        <button
          class="pos-action-btn"
          :tabindex="actionBarFocusIndex === 1 ? 0 : -1"
          data-action-index="1"
          :disabled="!hasItems"
          @click="showDiscountModal = true"
          @focus="actionBarFocusIndex = 1"
        >
          <span class="pos-action-key">F4</span>
          <span>{{ $t('pos.discount') }}</span>
        </button>
        <button
          class="pos-action-btn"
          :tabindex="actionBarFocusIndex === 2 ? 0 : -1"
          data-action-index="2"
          :disabled="!hasItems"
          @click="holdTransaction"
          @focus="actionBarFocusIndex = 2"
        >
          <span class="pos-action-key">F5</span>
          <span>{{ $t('pos.hold') }}</span>
        </button>
        <button
          class="pos-action-btn"
          :class="{ 'pos-action-confirm': clearConfirmPending }"
          :tabindex="actionBarFocusIndex === 3 ? 0 : -1"
          data-action-index="3"
          :disabled="!hasItems"
          @click="confirmClearCart"
          @focus="actionBarFocusIndex = 3"
        >
          <span class="pos-action-key">F6</span>
          <span>{{ clearConfirmPending ? 'Confirm?' : $t('pos.clear') }}</span>
        </button>
        <button
          v-if="heldTransactions.length > 0"
          class="pos-action-btn pos-action-held"
          :tabindex="actionBarFocusIndex === 4 ? 0 : -1"
          data-action-index="4"
          @click="showHeldModal = true"
          @focus="actionBarFocusIndex = 4"
        >
          <span class="pos-action-key">F7</span>
          <span>{{ $t('pos.recall') }} ({{ heldTransactions.length }})</span>
        </button>
      </div>
      <div class="pos-action-right">
        <button
          class="pos-action-btn pos-action-pay"
          :tabindex="actionBarFocusIndex === 5 ? 0 : -1"
          data-action-index="5"
          :disabled="!hasItems"
          @click="openPaymentPanel"
          @focus="actionBarFocusIndex = 5"
        >
          <span class="pos-action-key">F8</span>
          <span>{{ $t('pos.pay') }} ₹{{ formatPrice(grandTotal) }}</span>
        </button>
      </div>
    </footer>

    <!-- Keyboard Shortcuts Help Modal -->
    <Teleport to="body">
      <div v-if="showHelpModal" class="pos-modal-overlay" @click.self="showHelpModal = false">
        <div class="pos-modal pos-modal-help">
          <div class="pos-modal-header">
            <h3>{{ $t('pos.keyboardShortcuts') }}</h3>
            <button @click="showHelpModal = false">×</button>
          </div>
          <div class="pos-help-content">
            <div class="pos-help-section">Product Entry</div>
            <div class="pos-help-row"><kbd>F2</kbd> <span>Focus search / Toggle Cash-Credit in payment panel</span></div>
            <div class="pos-help-section">Cart</div>
            <div class="pos-help-row"><kbd>&uarr;</kbd><kbd>&darr;</kbd> <span>Select cart item</span></div>
            <div class="pos-help-row"><kbd>+</kbd><kbd>-</kbd> <span>Adjust qty</span></div>
            <div class="pos-help-row"><kbd>F9</kbd> <span>Focus qty input</span></div>
            <div class="pos-help-row"><kbd>Del</kbd> <span>Remove item</span></div>
            <div class="pos-help-section">Field Navigation (inside cart item)</div>
            <div class="pos-help-row"><kbd>Tab</kbd> <span>Next field: Qty → Price → Disc → next item</span></div>
            <div class="pos-help-row"><kbd>Shift+Tab</kbd> <span>Previous field / back to row</span></div>
            <div class="pos-help-row"><kbd>&uarr;</kbd><kbd>&darr;</kbd> <span>Same field, different item</span></div>
            <div class="pos-help-row"><kbd>Enter</kbd> <span>Confirm value, advance to next field</span></div>
            <div class="pos-help-row"><kbd>Esc</kbd> <span>Back to row selection</span></div>
            <div class="pos-help-section">Navigation</div>
            <div class="pos-help-row"><kbd>&darr;</kbd> <span>Customer → Search → Cart → Action Bar</span></div>
            <div class="pos-help-row"><kbd>Enter</kbd> / <kbd>&rarr;</kbd> <span>Enter fields (Qty → Price → Disc)</span></div>
            <div class="pos-help-row"><kbd>Backspace</kbd> <span>Clear selected customer (when search empty)</span></div>
            <div class="pos-help-row"><kbd>F3</kbd> <span>Customer search</span></div>
            <div class="pos-help-section">Actions</div>
            <div class="pos-help-row"><kbd>F4</kbd> <span>Discount</span></div>
            <div class="pos-help-row"><kbd>F5</kbd> <span>Hold</span></div>
            <div class="pos-help-row"><kbd>F6</kbd> <span>Clear cart</span></div>
            <div class="pos-help-row"><kbd>F7</kbd> <span>Recall held</span></div>
            <div class="pos-help-row"><kbd>F8</kbd> <span>Pay</span></div>
            <div class="pos-help-section">Payment</div>
            <div class="pos-help-row"><kbd>C</kbd><kbd>U</kbd> <span>Cash / UPI</span></div>
            <div class="pos-help-row"><kbd>F2</kbd> <span>Toggle Cash / Credit sale type</span></div>
            <div class="pos-help-row"><kbd>+</kbd> <span>Fill exact amount</span></div>
            <div class="pos-help-row"><kbd>1</kbd>-<kbd>6</kbd> <span>Quick cash (when Cash mode)</span></div>
            <div class="pos-help-row"><kbd>Enter</kbd> <span>Complete sale</span></div>
            <div class="pos-help-row"><kbd>Esc</kbd> <span>Back to cart</span></div>
            <div class="pos-help-section">Receipt</div>
            <div class="pos-help-row"><kbd>P</kbd> <span>Print receipt</span></div>
            <div class="pos-help-row"><kbd>D</kbd> <span>Download PDF</span></div>
            <div class="pos-help-row"><kbd>Enter</kbd> / <kbd>Esc</kbd> / <kbd>Space</kbd> <span>New sale</span></div>
            <div class="pos-help-section">Discount (F4)</div>
            <div class="pos-help-row"><kbd>Tab</kbd> <span>Switch Invoice / Item discount</span></div>
            <div class="pos-help-row"><kbd>%</kbd> <span>Percentage mode</span></div>
            <div class="pos-help-row"><kbd>R</kbd> <span>Fixed (Rs) mode</span></div>
            <div class="pos-help-row"><kbd>1</kbd>-<kbd>4</kbd> <span>Quick: 5% / 10% / 15% / 20%</span></div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Discount Modal (F4) -->
    <Teleport to="body">
      <div v-if="showDiscountModal" class="pos-modal-overlay" @click.self="showDiscountModal = false" @keydown="handleDiscountKeydown">
        <div class="pos-modal pos-modal-discount">
          <div class="pos-modal-header">
            <h3>{{ $t('pos.discount') }}</h3>
            <button @click="showDiscountModal = false">x</button>
          </div>
          <div class="pos-discount-content">
            <div class="pos-discount-tabs">
              <button
                class="pos-discount-tab"
                :class="{ active: discountTab === 'invoice' }"
                @click="discountTab = 'invoice'"
              >
                Invoice Discount [Tab]
              </button>
              <button
                v-if="selectedCartItemIndex >= 0"
                class="pos-discount-tab"
                :class="{ active: discountTab === 'item' }"
                @click="discountTab = 'item'"
              >
                Item: {{ cartItems[selectedCartItemIndex]?.product_name }} [Tab]
              </button>
            </div>
            <div class="pos-discount-form">
              <div class="pos-discount-type-row">
                <button
                  class="pos-discount-type-btn"
                  :class="{ active: discountFormType === 'percentage' }"
                  @click="discountFormType = 'percentage'"
                >
                  % [%]
                </button>
                <button
                  class="pos-discount-type-btn"
                  :class="{ active: discountFormType === 'fixed' }"
                  @click="discountFormType = 'fixed'"
                >
                  Rs [R]
                </button>
              </div>
              <input
                ref="discountInputRef"
                v-model.number="discountFormValue"
                type="number"
                min="0"
                step="0.01"
                class="pos-discount-input"
                placeholder="Enter discount value"
                @keydown.enter="applyDiscountAndClose"
                @keydown.escape="showDiscountModal = false"
              />
              <div class="pos-discount-quick-btns">
                <button v-for="(pct, qi) in [5, 10, 15, 20]" :key="pct" class="pos-discount-quick" @click="discountFormType = 'percentage'; discountFormValue = pct; applyDiscountAndClose()">
                  <span class="pos-action-key">{{ qi + 1 }}</span> {{ pct }}%
                </button>
              </div>
              <IntercomButton variant="primary" block @click="applyDiscountAndClose">
                Apply [Enter]
              </IntercomButton>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Held Transactions Modal (F7) -->
    <Teleport to="body">
      <div v-if="showHeldModal" class="pos-modal-overlay" @click.self="showHeldModal = false" @keydown="handleHeldKeydown">
        <div class="pos-modal pos-modal-held">
          <div class="pos-modal-header">
            <h3>{{ $t('pos.recall') }} ({{ heldTransactions.length }})</h3>
            <button @click="showHeldModal = false">x</button>
          </div>
          <div class="pos-held-list" v-if="heldTransactions.length > 0">
            <div
              v-for="(held, hi) in heldTransactions"
              :key="held.id"
              class="pos-held-item"
              :class="{ highlighted: heldHighlightIndex === hi }"
              @click="recallHeldAndClose(hi)"
            >
              <div class="pos-held-item-info">
                <span class="pos-held-item-customer">{{ held.customer?.name || 'Walk-in' }}</span>
                <span class="pos-held-item-time">{{ formatHeldTime(held.timestamp) }}</span>
              </div>
              <div class="pos-held-item-summary">
                <span>{{ held.items.length }} items</span>
                <span class="pos-held-item-total">Rs {{ formatPrice(held.total) }}</span>
              </div>
              <button class="pos-held-item-delete" @click.stop="deleteHeldAndRefresh(hi)" title="Delete">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div v-else class="pos-held-empty">
            No held transactions
          </div>
          <div class="pos-held-hint">
            <kbd>Up</kbd>/<kbd>Down</kbd> navigate, <kbd>Enter</kbd> recall, <kbd>Del</kbd> remove
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Print Receipt Modal -->
    <Teleport to="body">
      <div v-if="showPrintModal" class="pos-print-overlay" tabindex="-1" ref="printOverlayRef" @keydown="handlePrintKeydown">
        <div class="pos-print-toolbar">
          <div class="pos-print-sale-done">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Sale Complete — {{ printInvoiceData?.invoice_number }}</span>
          </div>
          <div class="pos-print-actions">
            <button class="pos-print-new-sale-btn" ref="newSaleBtnRef" @click="dismissPrintAndFocus">
              New Sale [Enter]
            </button>
            <button class="pos-print-btn" @click="handleDownloadReceipt">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download [D]
            </button>
            <button class="pos-print-btn" @click="handlePrintReceipt">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Print [P]
            </button>
          </div>
        </div>
        <div class="pos-print-content" :class="{ 'preview-a5': paperSize === 'A5' }">
          <TaxInvoicePrint
            v-if="printInvoiceData?.invoice_type === 'tax_invoice'"
            :invoice-data="printInvoiceData"
            :paper-size="paperSize"
          />
          <CashMemoPrint
            v-else-if="printInvoiceData"
            :invoice-data="printInvoiceData"
            :paper-size="paperSize"
          />
        </div>
      </div>
    </Teleport>

    <!-- Navigation Drawer -->
    <Transition name="drawer">
      <div v-if="showNavDrawer" class="pos-nav-overlay" @click.self="showNavDrawer = false">
        <div class="pos-nav-drawer">
          <div class="pos-nav-header">
            <span class="pos-nav-title">Navigation</span>
            <button class="pos-nav-close" @click="showNavDrawer = false">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <nav class="pos-nav-content">
            <div v-for="section in navSections" :key="section.label" class="pos-nav-section">
              <div class="pos-nav-section-label">{{ section.label }}</div>
              <router-link
                v-for="item in section.items"
                :key="item.path"
                :to="item.path"
                class="pos-nav-item"
                @click="showNavDrawer = false"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" v-html="item.icon"></svg>
                <span>{{ item.label }}</span>
              </router-link>
            </div>
          </nav>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { usePrint } from '@shared/composables/usePrint.js'
import { useBilling } from '../composables/useBilling.js'
import { usePOSStore } from '../composables/usePOSStore.js'
import { billingNavSections } from '../config/navigation.js'
import { PAYMENT_MODES, getLastPaymentMode, saveLastPaymentMode, flattenPrintData } from '../utils/billingUtils.js'
import { IntercomInput, IntercomButton, IntercomToggle, IntercomPhoneInput } from '@shared/components/intercom'
import TaxInvoicePrint from '../components/TaxInvoicePrint.vue'
import CashMemoPrint from '../components/CashMemoPrint.vue'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, logout } = useAuth()
const { listProducts, listCategories, listCustomers, createCustomer, createInvoice, finalizeInvoice, createPayment, getInvoicePrint, getLoyaltySettings, getBusiness, businessName, fetchBusinessName } = useBilling()
const {
  cartItems,
  selectedCustomer,
  selectedCartItemIndex,
  isPaymentPanelOpen,
  heldTransactions,
  loyaltyPointsToRedeem,
  loyaltySettings,
  applyGST,
  businessTaxRegime,
  subtotal,
  taxableSubtotal,
  taxBreakdown,
  invoiceDiscount,
  itemDiscountTotal,
  loyaltyDiscountAmount,
  grandTotal,
  hasItems,
  addProduct,
  incrementQuantity,
  decrementQuantity,
  removeItem,
  setCustomer,
  setLoyaltySettings,
  setLoyaltyRedemption,
  setBusinessTaxRegime,
  setApplyGST,
  clearCart,
  holdTransaction: posHoldTransaction,
  recallTransaction,
  buildInvoiceData,
  openPaymentPanel,
  closePaymentPanel,
  selectCartItem,
  updateQuantity,
  applyInvoiceDiscount,
  applyItemDiscount,
  updateUnitPrice,
  deleteHeldTransaction,
} = usePOSStore()

// Refs
const barcodeInputRef = ref(null)
const searchInputRef = ref(null)
const cartItemsRef = ref(null)
const amountInputRef = ref(null)
const customerSearchRef = ref(null)
const newCustomerNameRef = ref(null)
const newCustomerPhoneRef = ref(null)
const saleTypeCashRef = ref(null)

// Customer search state (HSN-style unified search-or-create)
const customerSearchQuery = ref('')
const customerSearchResults = ref([])
const customerDropdownOpen = ref(false)
const customerHighlight = ref(0)
let customerSearchTimer = null

// State
const currentTime = ref('')
const products = ref([])
const categories = ref([])
const customers = ref([])
const isLoadingProducts = ref(false)
const searchQuery = ref('')
const selectedCategory = ref(null)
const barcodeBuffer = ref('')
const lastKeyTime = ref(0)
const qtyMultiplier = ref(1) // For "3*" prefix: type 3, then *, next add uses qty=3
const qtyMultiplierTimer = ref(null)
const searchDropdownOpen = ref(false)
const searchHighlight = ref(0)
const editingQtyIndex = ref(-1) // F9: index of cart item being qty-edited
const editingQtyValue = ref('')  // F9: the typed qty string
const editingPriceIndex = ref(-1) // index of cart item being price-edited
const editingPriceValue = ref('')  // the typed price string

// Action bar roving tabindex (0=F3 Customer, 1=F4 Discount, 2=F5 Hold, 3=F6 Clear, 4=F7 Recall, 5=F8 Pay)
const actionBarFocusIndex = ref(0)

// Cart item sub-focus: which part of the selected cart item has sub-focus
// -1=item row, 0=decrement, 1=qty, 2=increment, 3=delete
const cartItemSubFocus = ref(-1)

// Navigation drawer
const showNavDrawer = ref(false)
const navSections = billingNavSections

// Customer dropdown and inline form
const customerOptions = ref([])
const isLoadingCustomers = ref(false)
const showNewCustomerForm = ref(false)
const customerCreateMode = ref('phone-first') // 'phone-first' (need name) or 'name-first' (need phone)
const isCreatingCustomer = ref(false)
const newCustomer = ref({ name: '', phone: '' })
const phoneError = ref('')
const posPhoneValid = ref(false)

// Modals
const showDiscountModal = ref(false)
const showHeldModal = ref(false)
const showHelpModal = ref(false)
const showCameraScanner = ref(false)

// Discount modal state
const discountTab = ref('invoice') // 'invoice' or 'item'
const discountFormType = ref('percentage')
const discountFormValue = ref(0)
const discountInputRef = ref(null)

// Held modal state
const heldHighlightIndex = ref(0)

// Payment — remember last used mode across sessions (saves 1 click per transaction)
const selectedPaymentMode = ref(getLastPaymentMode())
const amountReceived = ref(0)
const paymentReference = ref('')
const isProcessingPayment = ref(false)
const saleConfirmPending = ref(false)
let saleConfirmTimer = null
const saleType = ref('cash') // 'cash' or 'credit' (Khata/Udhar)

// Print receipt modal
const showPrintModal = ref(false)
const printInvoiceData = ref(null)
const printOverlayRef = ref(null)
const newSaleBtnRef = ref(null)

// Focus restoration: save focus before opening modals, restore on close
const lastFocusedBeforeModal = ref(null)

const saveFocus = () => {
  lastFocusedBeforeModal.value = document.activeElement
}

const restoreFocus = () => {
  if (lastFocusedBeforeModal.value && typeof lastFocusedBeforeModal.value.focus === 'function') {
    nextTick(() => lastFocusedBeforeModal.value.focus())
  }
  lastFocusedBeforeModal.value = null
}
// Print/PDF — shared composable (window.print for POS, not iframe — Chrome drops CSS vars in iframe)
const { paperSize, printDirect: handlePrintReceipt } = usePrint()

const handleDownloadReceipt = async () => {
  // Use server-side PDF generation (same as InvoicesView)
  if (!printInvoiceData.value?.id) return
  try {
    const { apiClient } = await import('@shared/utils/apiClient.js')
    const response = await apiClient.client.get(
      `/v1/invoices/${printInvoiceData.value.id}/pdf?paper_size=${paperSize.value}`,
      { responseType: 'blob' }
    )
    const url = URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `${printInvoiceData.value.invoice_number || 'receipt'}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
  }
}

const paymentModes = PAYMENT_MODES

// Computed
const filteredProducts = computed(() => {
  let filtered = products.value

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter((p) => p.category_id === selectedCategory.value)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query) ||
        p.barcode?.includes(query)
    )
  }

  return filtered
})

const changeDue = computed(() => {
  return Math.max(0, Number(amountReceived.value) - grandTotal.value)
})

// Quick-cash denomination buttons (Indian currency)
const quickCashDenominations = computed(() => {
  const due = grandTotal.value
  if (due <= 0) return []
  const denoms = []
  // Exact
  denoms.push({ label: 'Exact', amount: due, key: '1' })
  // Round up to nearest 10, 50, 100, 500, 1000, 2000
  const roundUps = [10, 50, 100, 500, 1000, 2000]
  let keyNum = 2
  for (const d of roundUps) {
    const rounded = Math.ceil(due / d) * d
    if (rounded > due && rounded !== denoms[denoms.length - 1]?.amount) {
      denoms.push({ label: `${rounded}`, amount: rounded, key: String(keyNum++) })
      if (keyNum > 6) break
    }
  }
  return denoms
})

// Methods
const formatPrice = (value) => {
  return parseFloat(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const updateTime = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Navigate back to Dashboard
const goBack = () => {
  router.push('/')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

const fetchData = async () => {
  isLoadingProducts.value = true
  try {
    const [productsRes, categoriesRes, customersRes, loyaltyRes, businessRes] = await Promise.all([
      listProducts({ limit: 1000 }),
      listCategories({ limit: 100 }),
      listCustomers({ limit: 20 }),
      getLoyaltySettings().catch(() => null), // Graceful fallback if no settings configured
      getBusiness().catch(() => null), // Fetch business for tax regime
    ])
    // API returns {data: [...], pagination: {...}}, axios wraps in response.data
    products.value = productsRes?.data?.data || productsRes?.data || []
    categories.value = categoriesRes?.data?.data || categoriesRes?.data || []
    const customersList = customersRes?.data?.data || customersRes?.data || []
    customers.value = customersList

    // Populate customer dropdown options
    customerOptions.value = customersList.map(c => ({
      label: `${c.name}${c.phone ? ' - ' + c.phone : ''}`,
      value: c.id,
      customer: c
    }))

    // Set loyalty settings (may be null if not configured)
    if (loyaltyRes) {
      setLoyaltySettings(loyaltyRes)
    }

    // Set business tax regime for per-invoice GST toggle
    if (businessRes) {
      const business = businessRes.business || businessRes.data || businessRes
      setBusinessTaxRegime(business.tax_regime || 'non_gst')
    }
  } catch (error) {
    console.error('Error fetching POS data:', error)
  } finally {
    isLoadingProducts.value = false
  }
}

// Barcode handling
const handleBarcodeInput = (e) => {
  const now = Date.now()
  const timeDiff = now - lastKeyTime.value

  // If input is rapid (< 50ms), it's likely a barcode scanner
  if (timeDiff < 50 || barcodeBuffer.value.length === 0) {
    barcodeBuffer.value = e.target.value
  }
  lastKeyTime.value = now
}

const processBarcodeBuffer = async () => {
  const raw = barcodeBuffer.value.trim()
  barcodeBuffer.value = ''
  barcodeInputRef.value.value = ''

  if (raw.length < 4) return

  // Parse QTY*BARCODE pattern (e.g., "3*8901234567890")
  let qty = qtyMultiplier.value
  let barcode = raw
  const qtyMatch = raw.match(/^(\d+)\*(.+)/)
  if (qtyMatch) {
    qty = parseInt(qtyMatch[1]) || 1
    barcode = qtyMatch[2]
  }

  // Find product by barcode
  const product = products.value.find((p) => p.barcode === barcode)
  if (product) {
    addProduct(product, qty)
    qtyMultiplier.value = 1
    if (qtyMultiplierTimer.value) clearTimeout(qtyMultiplierTimer.value)
    playBeep('success')
  } else {
    playBeep('error')
    console.warn('Product not found for barcode:', barcode)
  }
}

const playBeep = (type) => {
  // Simple beep using Web Audio API
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
    const oscillator = audioCtx.createOscillator()
    const gainNode = audioCtx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioCtx.destination)

    oscillator.frequency.value = type === 'success' ? 800 : 300
    oscillator.type = 'sine'
    gainNode.gain.value = 0.1

    oscillator.start()
    oscillator.stop(audioCtx.currentTime + 0.1)
  } catch (e) {
    // Audio not supported
  }
}

// Search
const handleSearch = () => {
  searchHighlight.value = 0
  searchDropdownOpen.value = searchQuery.value.length > 0
}

const handleSearchKeydown = (e) => {
  const maxIdx = Math.min(filteredProducts.value.length, 10) - 1

  switch (e.key) {
    case 'Tab':
      e.preventDefault()
      e.stopPropagation()
      searchDropdownOpen.value = false
      if (e.shiftKey) {
        // Shift+Tab → customer search
        customerSearchRef.value?.focus()
      } else {
        // Tab → first cart item (if items) or action bar
        if (hasItems.value) {
          selectCartItem(0)
          cartItemSubFocus.value = -1
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[0]?.focus()
          })
        } else {
          focusActionBar()
        }
      }
      break
    case 'ArrowDown':
      e.preventDefault()
      e.stopPropagation()
      if (searchQuery.value && searchHighlight.value < maxIdx) {
        // Navigate down within dropdown
        searchHighlight.value++
      } else {
        // Past last dropdown item OR no search text — advance to cart/action bar
        searchDropdownOpen.value = false
        if (hasItems.value) {
          selectCartItem(0)
          cartItemSubFocus.value = -1
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[0]?.focus()
          })
        } else {
          focusActionBar()
        }
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      e.stopPropagation()
      if (searchQuery.value && searchHighlight.value > 0) {
        // Navigate up within dropdown
        searchHighlight.value--
      } else {
        // Past first dropdown item OR no search text — go to customer search
        searchDropdownOpen.value = false
        customerSearchRef.value?.focus()
      }
      break
    case 'Enter':
      e.preventDefault()
      e.stopPropagation()
      // Empty search + Enter → advance to cart or action bar (don't add unfiltered product)
      if (!searchQuery.value) {
        if (hasItems.value) {
          selectCartItem(0)
          cartItemSubFocus.value = -1
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[0]?.focus()
          })
        } else {
          focusActionBar()
        }
      } else {
        handleSearchSelect()
      }
      break
    case 'Escape':
      e.preventDefault()
      if (searchQuery.value) {
        searchQuery.value = ''
        searchDropdownOpen.value = false
      } else {
        searchInputRef.value?.blur()
      }
      break
  }
}

const handleSearchSelect = () => {
  if (showNewCustomerForm.value) {
    showNewCustomerForm.value = false
  }

  // Parse "3*sugar" pattern: qty prefix in search
  let qty = qtyMultiplier.value
  let query = searchQuery.value
  const qtyMatch = query.match(/^(\d+)\*(.+)/)
  if (qtyMatch) {
    qty = parseInt(qtyMatch[1]) || 1
    query = qtyMatch[2]
    searchQuery.value = query
  }

  const idx = searchHighlight.value
  const prods = filteredProducts.value
  if (idx < prods.length && prods[idx].stock_quantity > 0) {
    // If qty was explicit (3*sugar), add directly without qty prompt
    const hasExplicitQty = qtyMatch || qtyMultiplier.value > 1
    addProduct(prods[idx], qty)
    qtyMultiplier.value = 1
    if (qtyMultiplierTimer.value) clearTimeout(qtyMultiplierTimer.value)
    searchQuery.value = ''
    searchDropdownOpen.value = false
    searchHighlight.value = 0
    // Auto-open qty edit so user can type quantity immediately
    if (!hasExplicitQty) {
      openQtyEditForSelectedItem()
    }
  }
}

const addProductFromSearch = (product) => {
  if (product.stock_quantity <= 0) return
  const hasExplicitQty = qtyMultiplier.value > 1
  addProduct(product, qtyMultiplier.value)
  qtyMultiplier.value = 1
  if (qtyMultiplierTimer.value) clearTimeout(qtyMultiplierTimer.value)
  searchQuery.value = ''
  searchDropdownOpen.value = false
  searchHighlight.value = 0
  // Auto-open qty edit so user can type quantity immediately
  if (!hasExplicitQty) {
    openQtyEditForSelectedItem()
  } else {
    searchInputRef.value?.focus()
  }
}

const closeSearchDropdown = () => {
  setTimeout(() => { searchDropdownOpen.value = false }, 150)
}

const clearSearch = () => {
  searchQuery.value = ''
  searchDropdownOpen.value = false
  searchHighlight.value = 0
  searchInputRef.value?.focus()
}

// Sub-focus fields: 0=qty, 1=price, 2=disc (editable inputs navigable by Arrow/Tab)
const FIELD_COUNT = 3

// Cart item keyboard navigation (when cart item row has focus, NOT inside an input)
const handleCartItemKeydown = (e, index) => {
  switch (e.key) {
    case 'Tab':
      e.preventDefault()
      e.stopPropagation()
      if (e.shiftKey) {
        searchInputRef.value?.focus()
      } else {
        // Tab → focus first field (qty) inside this item
        cartItemSubFocus.value = 0
        focusCartItemField(index, 0)
      }
      break
    case 'ArrowDown':
      e.preventDefault()
      e.stopPropagation()
      cartItemSubFocus.value = -1
      if (index < cartItems.value.length - 1) {
        selectCartItem(index + 1)
        scrollCartItemIntoView(index + 1)
        nextTick(() => {
          cartItemsRef.value?.querySelectorAll('.pos-cart-item')[index + 1]?.focus()
        })
      } else {
        focusActionBar()
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      e.stopPropagation()
      cartItemSubFocus.value = -1
      if (index > 0) {
        selectCartItem(index - 1)
        scrollCartItemIntoView(index - 1)
        nextTick(() => {
          cartItemsRef.value?.querySelectorAll('.pos-cart-item')[index - 1]?.focus()
        })
      } else {
        searchInputRef.value?.focus()
      }
      break
    case 'ArrowRight':
      e.preventDefault()
      e.stopPropagation()
      // Enter first field
      cartItemSubFocus.value = 0
      focusCartItemField(index, 0)
      break
    case 'Enter':
    case ' ':
      e.preventDefault()
      e.stopPropagation()
      // Enter on row → focus qty input
      cartItemSubFocus.value = 0
      focusCartItemField(index, 0)
      break
    case '+':
    case '=':
      e.preventDefault()
      e.stopPropagation()
      handleIncrement(index)
      break
    case '-':
      e.preventDefault()
      e.stopPropagation()
      decrementQuantity(index)
      break
    case 'Delete':
      e.preventDefault()
      e.stopPropagation()
      removeItem(index)
      refocusAfterCartRemoval(index)
      break
    case 'Escape':
      e.preventDefault()
      e.stopPropagation()
      // Escape on row → back to search
      searchInputRef.value?.focus()
      break
  }
}

// Keyboard navigation INSIDE a field input (qty/price/disc)
// Tab/ArrowRight → next field, Shift+Tab/ArrowLeft → prev field, ArrowUp/Down → change row, Escape → back to row
const handleFieldKeydown = (e, itemIndex, fieldIndex) => {
  switch (e.key) {
    case 'Tab':
      e.preventDefault()
      if (e.shiftKey) {
        // Shift+Tab: prev field or back to row
        if (fieldIndex > 0) {
          cartItemSubFocus.value = fieldIndex - 1
          focusCartItemField(itemIndex, fieldIndex - 1)
        } else {
          // Back to row focus
          cartItemSubFocus.value = -1
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[itemIndex]?.focus()
          })
        }
      } else {
        // Tab: next field or next row/action bar
        if (fieldIndex < FIELD_COUNT - 1) {
          cartItemSubFocus.value = fieldIndex + 1
          focusCartItemField(itemIndex, fieldIndex + 1)
        } else if (itemIndex < cartItems.value.length - 1) {
          // Jump to next row's first field
          selectCartItem(itemIndex + 1)
          scrollCartItemIntoView(itemIndex + 1)
          cartItemSubFocus.value = 0
          focusCartItemField(itemIndex + 1, 0)
        } else {
          // Last field of last item → action bar
          cartItemSubFocus.value = -1
          focusActionBar()
        }
      }
      break
    case 'ArrowRight':
      // Only navigate if cursor is at end of input
      if (e.target.selectionStart === e.target.value.length && fieldIndex < FIELD_COUNT - 1) {
        e.preventDefault()
        cartItemSubFocus.value = fieldIndex + 1
        focusCartItemField(itemIndex, fieldIndex + 1)
      }
      break
    case 'ArrowLeft':
      // Only navigate if cursor is at start of input
      if (e.target.selectionStart === 0 && fieldIndex > 0) {
        e.preventDefault()
        cartItemSubFocus.value = fieldIndex - 1
        focusCartItemField(itemIndex, fieldIndex - 1)
      }
      break
    case 'ArrowDown':
      e.preventDefault()
      if (itemIndex < cartItems.value.length - 1) {
        selectCartItem(itemIndex + 1)
        scrollCartItemIntoView(itemIndex + 1)
        focusCartItemField(itemIndex + 1, fieldIndex)
      } else {
        focusActionBar()
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      if (itemIndex > 0) {
        selectCartItem(itemIndex - 1)
        scrollCartItemIntoView(itemIndex - 1)
        focusCartItemField(itemIndex - 1, fieldIndex)
      } else {
        searchInputRef.value?.focus()
      }
      break
    case 'Escape':
      e.preventDefault()
      // Back to row focus
      cartItemSubFocus.value = -1
      nextTick(() => {
        cartItemsRef.value?.querySelectorAll('.pos-cart-item')[itemIndex]?.focus()
      })
      break
    case 'Enter':
      e.preventDefault()
      // Confirm value and advance to next field
      e.target.blur() // triggers @change
      if (fieldIndex < FIELD_COUNT - 1) {
        cartItemSubFocus.value = fieldIndex + 1
        focusCartItemField(itemIndex, fieldIndex + 1)
      } else {
        // Last field → back to search for next item
        cartItemSubFocus.value = -1
        nextTick(() => searchInputRef.value?.focus())
      }
      break
  }
}

// After a cart item is removed, re-focus the next item or fall back to search
const refocusAfterCartRemoval = (removedIndex) => {
  // Immediate focus to prevent barcode input from stealing focus during the gap
  // Then nextTick to confirm the correct element after Vue re-renders
  if (cartItems.value.length === 0) {
    searchInputRef.value?.focus()
  }
  nextTick(() => {
    if (cartItems.value.length > 0) {
      const newIdx = Math.min(removedIndex, cartItems.value.length - 1)
      selectCartItem(newIdx)
      scrollCartItemIntoView(newIdx)
      const items = cartItemsRef.value?.querySelectorAll('.pos-cart-item')
      items?.[newIdx]?.focus()
    } else {
      searchInputRef.value?.focus()
    }
  })
}

// Focus a specific field input within a cart item by data-field index (0=qty, 1=price, 2=disc)
const focusCartItemField = (itemIndex, fieldIndex) => {
  nextTick(() => {
    const item = cartItemsRef.value?.querySelectorAll('.pos-cart-item')[itemIndex]
    if (!item) return
    const input = item.querySelector(`[data-field="${fieldIndex}"]`)
    if (input) {
      input.focus()
      input.select()
    }
  })
}

// Action bar navigation (roving tabindex)
const focusActionBar = () => {
  nextTick(() => {
    const footer = document.querySelector('.pos-action-bar')
    if (!footer) return
    const btns = footer.querySelectorAll('button[data-action-index]')
    // Focus the currently roving button
    const target = footer.querySelector(`button[data-action-index="${actionBarFocusIndex.value}"]`)
    if (target && !target.disabled) {
      target.focus()
    } else if (btns.length > 0) {
      // Find the Pay button (last one) or first enabled
      const pay = footer.querySelector('button[data-action-index="5"]')
      if (pay && !pay.disabled) {
        actionBarFocusIndex.value = 5
        pay.focus()
      } else {
        for (const btn of btns) {
          if (!btn.disabled) {
            actionBarFocusIndex.value = parseInt(btn.dataset.actionIndex)
            btn.focus()
            break
          }
        }
      }
    }
  })
}

const handleActionBarKeydown = (e) => {
  const footer = e.currentTarget
  const btns = Array.from(footer.querySelectorAll('button[data-action-index]'))
  const currentIdx = btns.findIndex(b => parseInt(b.dataset.actionIndex) === actionBarFocusIndex.value)

  switch (e.key) {
    case 'Tab': {
      e.preventDefault()
      e.stopPropagation()
      if (e.shiftKey) {
        // Shift+Tab → last cart item or product search
        if (hasItems.value) {
          const lastIdx = cartItems.value.length - 1
          selectCartItem(lastIdx)
          cartItemSubFocus.value = -1
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[lastIdx]?.focus()
          })
        } else {
          searchInputRef.value?.focus()
        }
      } else {
        // Tab → payment (if open) or customer (wrap)
        if (isPaymentPanelOpen.value) {
          const payInput = amountInputRef.value?.$el?.querySelector('input') || amountInputRef.value
          payInput?.focus()
        } else {
          customerSearchRef.value?.focus()
        }
      }
      break
    }
    case 'ArrowRight': {
      e.preventDefault()
      e.stopPropagation()
      // Find next enabled button
      for (let i = currentIdx + 1; i < btns.length; i++) {
        if (!btns[i].disabled) {
          actionBarFocusIndex.value = parseInt(btns[i].dataset.actionIndex)
          btns[i].focus()
          break
        }
      }
      break
    }
    case 'ArrowLeft': {
      e.preventDefault()
      e.stopPropagation()
      for (let i = currentIdx - 1; i >= 0; i--) {
        if (!btns[i].disabled) {
          actionBarFocusIndex.value = parseInt(btns[i].dataset.actionIndex)
          btns[i].focus()
          break
        }
      }
      break
    }
    case 'ArrowUp': {
      e.preventDefault()
      e.stopPropagation()
      // Jump to last cart item or product search if cart empty
      if (hasItems.value) {
        const lastIndex = cartItems.value.length - 1
        selectCartItem(lastIndex)
        cartItemSubFocus.value = -1
        scrollCartItemIntoView(lastIndex)
        nextTick(() => {
          cartItemsRef.value?.querySelectorAll('.pos-cart-item')[lastIndex]?.focus()
        })
      } else {
        searchInputRef.value?.focus()
      }
      break
    }
    case 'ArrowDown': {
      e.preventDefault()
      e.stopPropagation()
      // Action bar down → payment panel if open, else wrap to customer
      if (isPaymentPanelOpen.value) {
        const payInput = amountInputRef.value?.$el?.querySelector('input') || amountInputRef.value
        payInput?.focus()
      } else {
        customerSearchRef.value?.focus()
      }
      break
    }
    case 'Enter': {
      // Let the focused button handle its own click
      break
    }
  }
}

const handleProductClick = (product) => {
  if (product.stock_quantity <= 0) {
    return
  }
  // Auto-collapse new customer form when user starts adding products
  if (showNewCustomerForm.value) {
    showNewCustomerForm.value = false
  }
  addProduct(product, qtyMultiplier.value)
  qtyMultiplier.value = 1
  if (qtyMultiplierTimer.value) clearTimeout(qtyMultiplierTimer.value)
}

// Customer search-or-create (HSN-style unified field)
// Phone-first: digits require 10 chars (full phone), names require 3+ chars
const isDigitsOnly = (s) => /^\d+$/.test(s)

// Enforce 10-digit max when typing digits only
const onCustomerSearchInput = (e) => {
  let val = e.target.value
  // If all digits, cap at 10
  if (isDigitsOnly(val) && val.length > 10) {
    val = val.slice(0, 10)
    e.target.value = val
  }
  customerSearchQuery.value = val
  handleCustomerSearch()
}

const handleCustomerSearch = () => {
  if (customerSearchTimer) clearTimeout(customerSearchTimer)
  const q = customerSearchQuery.value?.trim()
  if (!q) {
    customerSearchResults.value = []
    customerDropdownOpen.value = false
    return
  }

  // Search after 2+ characters for both phone and name
  if (q.length < 2) {
    customerSearchResults.value = []
    customerDropdownOpen.value = false
    return
  }

  isLoadingCustomers.value = true
  customerDropdownOpen.value = true
  customerHighlight.value = 0
  customerSearchTimer = setTimeout(async () => {
    try {
      const response = await listCustomers({ search: q, limit: 10 })
      const customers = response?.data?.data || response?.data || []
      customerSearchResults.value = Array.isArray(customers) ? customers : []
      customerDropdownOpen.value = true

      // Auto-select on exact 10-digit phone match (saves one Enter keystroke)
      const currentQ = customerSearchQuery.value?.trim()
      if (
        isDigitsOnly(currentQ) &&
        currentQ.length === 10 &&
        customerSearchResults.value.length === 1 &&
        customerSearchResults.value[0].phone?.endsWith(currentQ)
      ) {
        selectCustomerResult(customerSearchResults.value[0])
        nextTick(() => { searchInputRef.value?.focus() })
      }
    } catch (error) {
      console.error('Failed to search customers:', error)
      customerSearchResults.value = []
    } finally {
      isLoadingCustomers.value = false
    }
  }, 250)
}

const handleCustomerKeydown = (e) => {
  switch (e.key) {
    case 'Tab':
      e.preventDefault()
      e.stopPropagation()
      if (e.shiftKey) {
        // Shift+Tab from customer → wrap to payment (if open) or action bar
        if (isPaymentPanelOpen.value) {
          const payInput = amountInputRef.value?.$el?.querySelector('input') || amountInputRef.value
          payInput?.focus()
        } else {
          focusActionBar()
        }
      } else {
        // Tab from customer → product search
        searchInputRef.value?.focus()
      }
      break
    case 'Backspace':
    case 'Delete':
      // If query is empty and customer is selected, clear the customer
      if (!customerSearchQuery.value && selectedCustomer.value) {
        e.preventDefault()
        e.stopPropagation()
        clearCustomerSelection()
      }
      // Otherwise let browser handle backspace in the input normally
      break
    case 'ArrowDown':
      e.preventDefault()
      e.stopPropagation()
      if (!customerSearchQuery.value && !customerDropdownOpen.value) {
        // Boundary: customer search → product search
        searchInputRef.value?.focus()
      } else {
        handleCustomerArrow(1)
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      e.stopPropagation()
      handleCustomerArrow(-1)
      break
    case 'Enter':
      e.preventDefault()
      e.stopPropagation()
      handleCustomerEnter()
      break
    case 'Escape':
      e.preventDefault()
      e.stopPropagation()
      if (customerDropdownOpen.value) {
        // First: close dropdown
        customerDropdownOpen.value = false
        customerSearchQuery.value = ''
      } else if (customerSearchQuery.value) {
        // Second: clear typed query
        customerSearchQuery.value = ''
      } else if (selectedCustomer.value) {
        // Third: clear selected customer
        clearCustomerSelection()
      }
      break
  }
}

const handleCustomerArrow = (dir) => {
  const q = customerSearchQuery.value?.trim() || ''

  // Open dropdown if not already open
  if (!customerDropdownOpen.value && q.length >= 2) {
    customerDropdownOpen.value = true
    // Also trigger search if not already done
    if (customerSearchResults.value.length === 0 && !isLoadingCustomers.value) {
      handleCustomerSearch()
    }
    return
  }

  // Navigate within dropdown
  // "Create" option sits at index = customerSearchResults.length
  const hasCreateOption = q.length >= 2
  const maxIdx = customerSearchResults.value.length + (hasCreateOption ? 0 : -1)
  if (maxIdx < 0) return
  customerHighlight.value = Math.max(0, Math.min(customerHighlight.value + dir, maxIdx))
}

const handleCustomerEnter = () => {
  // If inline create form is showing, submit it
  if (showNewCustomerForm.value) {
    createAndSelectCustomer()
    return
  }

  const q = customerSearchQuery.value?.trim() || ''

  // If dropdown is open with results, select the highlighted one
  if (customerDropdownOpen.value && customerSearchResults.value.length > 0) {
    const idx = customerHighlight.value
    if (idx < customerSearchResults.value.length) {
      selectCustomerResult(customerSearchResults.value[idx])
      nextTick(() => { searchInputRef.value?.focus() })
      return
    }
    // "Create new" option highlighted
    if (q.length >= 2) {
      startInlineCreate()
      return
    }
  }

  // 10 digits typed but no match found → directly start inline create
  if (isDigitsOnly(q) && q.length === 10 && customerSearchResults.value.length === 0) {
    startInlineCreate()
    return
  }

  // Partial phone number (digits but less than 10) → tell user to complete it
  if (isDigitsOnly(q) && q.length > 0 && q.length < 10) {
    toast.warning(`Enter full 10-digit phone number (${q.length}/10)`)
    return
  }

  // Name typed with no match → start inline create
  if (!isDigitsOnly(q) && q.length >= 2 && customerDropdownOpen.value && customerSearchResults.value.length === 0) {
    startInlineCreate()
    return
  }

  // No query / customer already selected → skip (walk-in), advance to product search
  customerDropdownOpen.value = false
  searchInputRef.value?.focus()
}

const selectCustomerResult = (cust) => {
  setCustomer(cust)
  customerSearchQuery.value = ''
  customerSearchResults.value = []
  customerDropdownOpen.value = false
  // Keep focus on customer field — next Enter will advance to product search
}

const clearCustomerSelection = () => {
  setCustomer(null)
  customerSearchQuery.value = ''
  customerSearchResults.value = []
}

const closeCustomerDropdown = () => {
  setTimeout(() => { customerDropdownOpen.value = false }, 150)
}

const startInlineCreate = () => {
  const q = customerSearchQuery.value.trim()
  // If typed 10 digits (phone), pre-fill phone and focus name
  // If typed a name, pre-fill name and focus phone
  if (isDigitsOnly(q) && q.length === 10) {
    newCustomer.value = { name: '', phone: '+91' + q }
    customerCreateMode.value = 'phone-first'
    showNewCustomerForm.value = true
    customerDropdownOpen.value = false
    customerSearchQuery.value = ''
    nextTick(() => {
      newCustomerNameRef.value?.focus()
    })
  } else {
    newCustomer.value = { name: q, phone: '' }
    customerCreateMode.value = 'name-first'
    showNewCustomerForm.value = true
    customerDropdownOpen.value = false
    customerSearchQuery.value = ''
    nextTick(() => {
      const phoneInput = newCustomerPhoneRef.value?.$el?.querySelector('input')
      if (phoneInput) phoneInput.focus()
    })
  }
}

const createAndSelectCustomer = async () => {
  if (!newCustomer.value.name) return
  // Only validate phone via IntercomPhoneInput in name-first mode
  // In phone-first mode, the phone was already validated as 10 digits
  if (customerCreateMode.value === 'name-first' && newCustomer.value.phone && !posPhoneValid.value) {
    phoneError.value = 'Phone must be exactly 10 digits'
    return
  }
  isCreatingCustomer.value = true
  try {
    let phone = newCustomer.value.phone || null
    if (phone && phone.startsWith('+91')) phone = phone.slice(3)

    const response = await createCustomer({
      name: newCustomer.value.name,
      phone: phone || null,
    })
    const created = response?.customer || response
    setCustomer(created)
    showNewCustomerForm.value = false
    newCustomer.value = { name: '', phone: '' }
    phoneError.value = ''
    // Advance to product search after creating customer
    nextTick(() => { searchInputRef.value?.focus() })
  } catch (error) {
    console.error('Failed to create customer:', error)
  } finally {
    isCreatingCustomer.value = false
  }
}

const cancelNewCustomer = () => {
  showNewCustomerForm.value = false
  newCustomer.value = { name: '', phone: '' }
  phoneError.value = ''
  nextTick(() => { customerSearchRef.value?.focus() })
}

// Handle keydown on the inline new customer form wrapper (catches Enter from IntercomPhoneInput)
const handleNewCustomerFormKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    e.stopPropagation()
    createAndSelectCustomer()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    cancelNewCustomer()
  }
}

// Cart
const clearConfirmPending = ref(false)
let clearConfirmTimer = null

const confirmClearCart = () => {
  if (clearConfirmPending.value) {
    // Second press — actually clear
    clearConfirmPending.value = false
    if (clearConfirmTimer) clearTimeout(clearConfirmTimer)
    clearCart()
    saleType.value = 'cash'
  } else {
    // First press — show confirmation state
    clearConfirmPending.value = true
    clearConfirmTimer = setTimeout(() => {
      clearConfirmPending.value = false
    }, 2000)
  }
}

const holdTransaction = () => {
  if (hasItems.value) {
    posHoldTransaction()
    // TODO: Show success toast
  }
}

// Payment
const confirmAndCompleteSale = () => {
  if (saleConfirmPending.value) {
    // Second press — actually complete
    saleConfirmPending.value = false
    if (saleConfirmTimer) clearTimeout(saleConfirmTimer)
    completeSale()
  } else {
    // First press — show confirmation
    saleConfirmPending.value = true
    if (saleConfirmTimer) clearTimeout(saleConfirmTimer)
    saleConfirmTimer = setTimeout(() => {
      saleConfirmPending.value = false
    }, 5000) // Auto-cancel after 5s
  }
}

const completeSale = async () => {
  if (isProcessingPayment.value) return

  // Validate based on sale type
  if (saleType.value === 'cash' && Number(amountReceived.value) < grandTotal.value) return
  if (saleType.value === 'credit' && !selectedCustomer.value) {
    toast.warning('Select a customer to save as credit bill')
    return
  }

  isProcessingPayment.value = true

  try {
    // 1. Create invoice
    const invoiceData = buildInvoiceData()
    invoiceData.sale_type = saleType.value
    if (saleType.value === 'cash') {
      invoiceData.payment_mode = selectedPaymentMode.value
    }

    const invoiceRes = await createInvoice(invoiceData)
    const invoiceId = invoiceRes.invoice?.id || invoiceRes.id

    // 2. Finalize invoice (decrements stock, auto-records payment for cash sales)
    const finalizeRes = await finalizeInvoice(invoiceId, {
      payment_mode: saleType.value === 'cash' ? selectedPaymentMode.value : undefined,
    })

    // Show low stock warnings if any products fell below threshold
    const warnings = finalizeRes.low_stock_warnings || finalizeRes.data?.low_stock_warnings
    if (warnings && warnings.length > 0) {
      const names = warnings.map(w => `${w.product_name} (${w.stock_quantity} left)`).join(', ')
      toast.warning(`Low stock: ${names}`, { duration: 6000 })
    }

    // 4. Get print data and show receipt modal
    const printRes = await getInvoicePrint(invoiceId)
    const rawData = printRes.data || printRes
    printInvoiceData.value = flattenPrintData(rawData)
    showPrintModal.value = true

    // 5. Remember payment mode for next transaction (cash only)
    if (saleType.value === 'cash') {
      saveLastPaymentMode(selectedPaymentMode.value)
    }

    // 6. Clear cart and close payment (clearCart also resets loyalty)
    const wasCredit = saleType.value === 'credit'
    clearCart()
    closePaymentPanel()
    amountReceived.value = 0
    paymentReference.value = ''
    saleType.value = 'cash'

    // 7. Refresh products to get updated stock quantities
    listProducts({ limit: 1000 }).then(res => {
      products.value = res?.data?.data || res?.data || []
    }).catch(() => {})

    toast.success(wasCredit ? 'Credit bill saved' : t('pos.saleComplete'))
  } catch (error) {
    console.error('Payment error:', error)
    toast.error('Payment failed: ' + (error.message || 'Unknown error'))
  } finally {
    isProcessingPayment.value = false
  }
}

// Dismiss print modal and auto-focus search for next sale
const dismissPrintAndFocus = () => {
  showPrintModal.value = false
  nextTick(() => {
    searchInputRef.value?.focus()
  })
}

// Print overlay keyboard handler (Teleported outside .pos-view, needs its own handler)
const handlePrintKeydown = (e) => {
  switch (e.key) {
    case 'Enter':
    case 'Escape':
    case ' ':
      e.preventDefault()
      dismissPrintAndFocus()
      break
    case 'p':
    case 'P':
      e.preventDefault()
      handlePrintReceipt()
      break
    case 'd':
    case 'D':
      e.preventDefault()
      handleDownloadReceipt()
      break
    case 'ArrowLeft':
    case 'ArrowRight': {
      // Navigate between the 3 buttons
      e.preventDefault()
      const btns = printOverlayRef.value?.querySelectorAll('.pos-print-actions button')
      if (!btns?.length) return
      const idx = Array.from(btns).indexOf(document.activeElement)
      const dir = e.key === 'ArrowRight' ? 1 : -1
      const next = (idx + dir + btns.length) % btns.length
      btns[next]?.focus()
      break
    }
  }
}

// Reset qty multiplier after timeout
const resetQtyMultiplier = () => {
  if (qtyMultiplierTimer.value) clearTimeout(qtyMultiplierTimer.value)
  qtyMultiplierTimer.value = setTimeout(() => {
    qtyMultiplier.value = 1
  }, 3000)
}

// Discount modal methods
const applyDiscountAndClose = () => {
  if (!discountFormValue.value || discountFormValue.value <= 0) {
    showDiscountModal.value = false
    return
  }
  if (discountTab.value === 'item' && selectedCartItemIndex.value >= 0) {
    applyItemDiscount(selectedCartItemIndex.value, discountFormType.value, discountFormValue.value)
  } else {
    applyInvoiceDiscount(discountFormType.value, discountFormValue.value)
  }
  showDiscountModal.value = false
  discountFormValue.value = 0
}

const handleSaleTypeKeydown = (e) => {
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    e.preventDefault()
    saleType.value = saleType.value === 'cash' ? 'credit' : 'cash'
    nextTick(() => {
      const activeBtn = document.querySelector('.sale-type-btn.active')
      activeBtn?.focus()
    })
  }
}

// Payment panel vertical navigation: ArrowUp/Down moves between sections, Tab exits
const handlePaymentPanelKeydown = (e) => {
  if (e.key === 'Tab') {
    e.preventDefault()
    e.stopPropagation()
    if (e.shiftKey) {
      focusActionBar()
    } else {
      customerSearchRef.value?.focus()
    }
    return
  }

  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

  // Collect all visible focusable elements inside the payment panel
  const panel = document.querySelector('.pos-payment-panel')
  if (!panel) return

  const focusables = Array.from(panel.querySelectorAll(
    'button:not([disabled]):not(.pos-back-btn), input:not([disabled]), [tabindex="0"]'
  )).filter(el => {
    // Only visible elements
    return el.offsetParent !== null
  })

  if (focusables.length === 0) return

  const activeEl = document.activeElement
  let currentIdx = focusables.indexOf(activeEl)

  // If focus is inside an IntercomInput wrapper, find the actual input
  if (currentIdx === -1) {
    currentIdx = focusables.findIndex(f => f.contains(activeEl) || activeEl.contains(f))
  }

  if (currentIdx === -1) {
    // Not on any focusable — focus the first one
    focusables[0]?.focus()
    e.preventDefault()
    e.stopPropagation()
    return
  }

  e.preventDefault()
  e.stopPropagation()

  if (e.key === 'ArrowDown') {
    if (currentIdx < focusables.length - 1) {
      focusables[currentIdx + 1].focus()
    } else {
      // Past last element → wrap to action bar
      focusActionBar()
    }
  } else {
    // ArrowUp
    if (currentIdx > 0) {
      focusables[currentIdx - 1].focus()
    } else {
      // Past first element → action bar
      focusActionBar()
    }
  }
}

const handleDiscountKeydown = (e) => {
  if (e.key === 'Escape') {
    e.preventDefault()
    showDiscountModal.value = false
    return
  }
  // Tab/Shift+Tab: switch between Invoice and Item discount tabs
  if (e.key === 'Tab' && selectedCartItemIndex.value >= 0) {
    e.preventDefault()
    discountTab.value = discountTab.value === 'invoice' ? 'item' : 'invoice'
    return
  }
  // % key: switch to percentage mode
  if (e.key === '%' && e.target.tagName !== 'INPUT') {
    e.preventDefault()
    discountFormType.value = 'percentage'
    nextTick(() => discountInputRef.value?.focus())
    return
  }
  // R key: switch to fixed (Rs) mode
  if ((e.key === 'r' || e.key === 'R') && e.target.tagName !== 'INPUT') {
    e.preventDefault()
    discountFormType.value = 'fixed'
    nextTick(() => discountInputRef.value?.focus())
    return
  }
  // Quick percentage shortcuts (1-4) when not in input
  if (e.target.tagName !== 'INPUT' && e.key >= '1' && e.key <= '4') {
    e.preventDefault()
    const pcts = [5, 10, 15, 20]
    const idx = parseInt(e.key) - 1
    discountFormType.value = 'percentage'
    discountFormValue.value = pcts[idx]
    applyDiscountAndClose()
    return
  }
}

// Held transactions modal methods
const recallHeldAndClose = (index) => {
  recallTransaction(index)
  showHeldModal.value = false
  heldHighlightIndex.value = 0
}

const deleteHeldAndRefresh = (index) => {
  deleteHeldTransaction(index)
  if (heldTransactions.value.length === 0) {
    showHeldModal.value = false
  } else if (heldHighlightIndex.value >= heldTransactions.value.length) {
    heldHighlightIndex.value = heldTransactions.value.length - 1
  }
}

const formatHeldTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
}

const handleHeldKeydown = (e) => {
  switch (e.key) {
    case 'ArrowUp':
      e.preventDefault()
      if (heldHighlightIndex.value > 0) heldHighlightIndex.value--
      break
    case 'ArrowDown':
      e.preventDefault()
      if (heldHighlightIndex.value < heldTransactions.value.length - 1) heldHighlightIndex.value++
      break
    case 'Enter':
      e.preventDefault()
      recallHeldAndClose(heldHighlightIndex.value)
      break
    case 'Delete':
    case 'Backspace':
      e.preventDefault()
      deleteHeldAndRefresh(heldHighlightIndex.value)
      break
    case 'Escape':
      e.preventDefault()
      showHeldModal.value = false
      break
  }
}

// Auto-scroll cart item into view when navigating with arrow keys
const scrollCartItemIntoView = (index) => {
  nextTick(() => {
    const container = cartItemsRef.value
    if (!container) return
    const items = container.querySelectorAll('.pos-cart-item')
    if (items[index]) {
      items[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  })
}

// Keyboard shortcuts
const handleKeydown = (e) => {
  // Print modal: any key dismisses and starts next sale
  if (showPrintModal.value) {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      dismissPrintAndFocus()
    } else if (e.key === 'p' || e.key === 'P') {
      e.preventDefault()
      handlePrintReceipt()
    } else if (e.key === 'd' || e.key === 'D') {
      e.preventDefault()
      handleDownloadReceipt()
    }
    return
  }

  // F9 qty edit mode: capture digits and Enter/Escape
  if (editingQtyIndex.value >= 0) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitQtyEdit()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      editingQtyIndex.value = -1
      editingQtyValue.value = ''
      return
    }
    // Let the input field handle other keys normally
    return
  }

  // Price edit mode: capture digits and Enter/Escape
  if (editingPriceIndex.value >= 0) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitPriceEdit()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      editingPriceIndex.value = -1
      editingPriceValue.value = ''
      return
    }
    return
  }

  // Tab navigation: distributed — each area handler owns its own Tab behavior
  // This global handler catches Tab from areas without their own keydown handler (e.g. payment panel)
  if (
    e.key === 'Tab' &&
    !showHelpModal.value &&
    !showDiscountModal.value &&
    !showHeldModal.value &&
    !showPrintModal.value &&
    !showNewCustomerForm.value
  ) {
    const activeEl = document.activeElement
    const inPayment = activeEl?.closest?.('.pos-payment-panel')
    const inActionBar = activeEl?.closest?.('.pos-action-bar')

    // Payment panel Tab: forward → customer (wrap), back → action bar
    if (inPayment) {
      e.preventDefault()
      if (e.shiftKey) {
        focusActionBar()
      } else {
        customerSearchRef.value?.focus()
      }
      return
    }

    // Action bar Tab is handled in handleActionBarKeydown, but as a safety net:
    if (inActionBar) {
      e.preventDefault()
      if (e.shiftKey) {
        if (hasItems.value) {
          const lastIdx = cartItems.value.length - 1
          selectCartItem(lastIdx)
          cartItemSubFocus.value = -1
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[lastIdx]?.focus()
          })
        } else {
          searchInputRef.value?.focus()
        }
      } else {
        if (isPaymentPanelOpen.value) {
          const payInput = amountInputRef.value?.$el?.querySelector('input') || amountInputRef.value
          payInput?.focus()
        } else {
          customerSearchRef.value?.focus()
        }
      }
      return
    }
  }

  // Don't trigger shortcuts when typing in real inputs (except F-keys and Esc)
  // The hidden barcode input is excluded — it should not block keyboard shortcuts
  const isBarcodeInput = e.target === barcodeInputRef.value
  const isInput = ['INPUT', 'TEXTAREA'].includes(e.target.tagName) && !isBarcodeInput
  const isFKey = e.key.startsWith('F')

  // Allow Enter through when payment panel is open (so cashier can press Enter from amount field)
  // Allow Enter and payment mode shortcuts (C/U/D/B) through from payment panel inputs
  const inPaymentPanel = isPaymentPanelOpen.value && e.target.closest('.pos-payment-panel')
  const allowPaymentKeys = inPaymentPanel && (e.key === 'Enter' || /^[cu]$/i.test(e.key))
  if (isInput && !isFKey && e.key !== 'Escape' && !allowPaymentKeys) return

  // Clear barcode buffer when processing a keyboard shortcut (prevents stale chars)
  if (isBarcodeInput) {
    barcodeBuffer.value = ''
    if (barcodeInputRef.value) barcodeInputRef.value.value = ''
  }

  switch (e.key) {
    case 'F1':
      e.preventDefault()
      saveFocus(); showHelpModal.value = true
      break
    case 'F2':
      e.preventDefault()
      if (isPaymentPanelOpen.value) {
        // In payment panel: toggle between cash and credit
        saleType.value = saleType.value === 'cash' ? 'credit' : 'cash'
        // Move focus to the newly active button
        nextTick(() => {
          const activeBtn = document.querySelector('.sale-type-btn.active')
          activeBtn?.focus()
        })
      } else {
        searchInputRef.value?.focus()
      }
      break
    case 'F3':
      e.preventDefault()
      if (showNewCustomerForm.value) {
        cancelNewCustomer()
      } else {
        customerSearchRef.value?.focus()
      }
      break
    case 'F4':
      e.preventDefault()
      if (hasItems.value) { saveFocus(); showDiscountModal.value = true }
      break
    case 'F5':
      e.preventDefault()
      holdTransaction()
      break
    case 'F6':
      e.preventDefault()
      if (hasItems.value) confirmClearCart()
      break
    case 'F7':
      e.preventDefault()
      if (heldTransactions.value.length > 0) { saveFocus(); showHeldModal.value = true }
      break
    case 'F8':
      e.preventDefault()
      if (hasItems.value) openPaymentPanel()
      // Focus + amount handled by isPaymentPanelOpen watcher
      break
    case 'F9':
      // Focus qty input of selected cart item
      e.preventDefault()
      if (selectedCartItemIndex.value >= 0) {
        cartItemSubFocus.value = 0
        focusCartItemField(selectedCartItemIndex.value, 0)
      }
      break
    case 'F11':
      // Delete selected cart item
      e.preventDefault()
      if (selectedCartItemIndex.value >= 0) {
        const f11Idx = selectedCartItemIndex.value
        removeItem(f11Idx)
        refocusAfterCartRemoval(f11Idx)
      }
      break
    case 'Escape':
      e.preventDefault()
      if (showNewCustomerForm.value) {
        cancelNewCustomer()
      } else if (editingQtyIndex.value >= 0) {
        editingQtyIndex.value = -1
        editingQtyValue.value = ''
      } else if (editingPriceIndex.value >= 0) {
        editingPriceIndex.value = -1
        editingPriceValue.value = ''
      } else if (isPaymentPanelOpen.value) {
        saleConfirmPending.value = false
        if (saleConfirmTimer) clearTimeout(saleConfirmTimer)
        closePaymentPanel()
        nextTick(() => searchInputRef.value?.focus())
      } else if (showHelpModal.value) {
        showHelpModal.value = false
      } else if (showDiscountModal.value) {
        showDiscountModal.value = false
      } else if (showHeldModal.value) {
        showHeldModal.value = false
      } else if (selectedCustomer.value) {
        // Nothing else to dismiss — clear selected customer
        clearCustomerSelection()
        customerSearchRef.value?.focus()
      }
      // Reset qty multiplier on Esc
      qtyMultiplier.value = 1
      break
    case '+':
    case '=':
      if (isPaymentPanelOpen.value && !isInput) {
        // Fill outstanding balance (Citrus Lime pattern)
        e.preventDefault()
        amountReceived.value = grandTotal.value
        nextTick(() => {
          amountInputRef.value?.focus()
          amountInputRef.value?.select()
        })
      } else if (!isInput && selectedCartItemIndex.value >= 0) {
        e.preventDefault()
        incrementQuantity(selectedCartItemIndex.value)
      }
      break
    case '-':
      if (!isInput && selectedCartItemIndex.value >= 0) {
        e.preventDefault()
        decrementQuantity(selectedCartItemIndex.value)
      }
      break
    case 'Delete':
      if (!isInput && selectedCartItemIndex.value >= 0) {
        e.preventDefault()
        const delIdx = selectedCartItemIndex.value
        removeItem(delIdx)
        refocusAfterCartRemoval(delIdx)
      }
      break
    case 'ArrowUp':
    case 'ArrowDown': {
      // Context-aware vertical navigation fallback
      // Specific handlers (customer, search, cart, action bar) stopPropagation,
      // so this only fires from unhandled areas (payment panel, barcode input, etc.)
      if (isInput && !isBarcodeInput) break // let native input behavior work
      e.preventDefault()
      const navEl = document.activeElement
      const inPayment = navEl?.closest?.('.pos-payment-panel')

      if (e.key === 'ArrowUp') {
        if (inPayment) {
          // Payment → action bar
          focusActionBar()
        } else if (hasItems.value) {
          // Fallback: focus last selected cart item
          const idx = Math.max(0, selectedCartItemIndex.value)
          selectCartItem(idx)
          cartItemSubFocus.value = -1
          scrollCartItemIntoView(idx)
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[idx]?.focus()
          })
        } else {
          searchInputRef.value?.focus()
        }
      } else {
        // ArrowDown
        if (inPayment) {
          // Already at bottom, wrap to customer
          customerSearchRef.value?.focus()
        } else if (hasItems.value) {
          const idx = Math.max(0, selectedCartItemIndex.value)
          selectCartItem(idx)
          cartItemSubFocus.value = -1
          scrollCartItemIntoView(idx)
          nextTick(() => {
            cartItemsRef.value?.querySelectorAll('.pos-cart-item')[idx]?.focus()
          })
        } else {
          focusActionBar()
        }
      }
      break
    }
    case '*':
      // Set qty multiplier: type "3*" = next add uses qty 3
      if (!isInput && qtyMultiplier.value > 1) {
        e.preventDefault()
        resetQtyMultiplier()
      }
      break
    case 'Enter':
      if (isPaymentPanelOpen.value) {
        // Allow Enter from any element within the payment panel (amount input, etc.)
        const inPaymentPanel = e.target.closest('.pos-payment-panel')
        if (!isInput || inPaymentPanel) {
          e.preventDefault()
          confirmAndCompleteSale()
        }
      }
      break
  }

  // * key confirms the qty multiplier (used with search: "3*rice")
  if (!isInput && e.key === '*' && qtyMultiplier.value > 1) {
    e.preventDefault()
    if (qtyMultiplierTimer.value) clearTimeout(qtyMultiplierTimer.value)
    // Keep multiplier alive for 5 seconds
    qtyMultiplierTimer.value = setTimeout(() => {
      qtyMultiplier.value = 1
    }, 5000)
    return
  }

  // Payment panel shortcuts
  if (isPaymentPanelOpen.value) {
    // Payment mode letter shortcuts (work from anywhere in payment panel)
    const modeKey = e.key.toLowerCase()
    if (['c', 'u'].includes(modeKey)) {
      e.preventDefault()
      const modes = { c: 'cash', u: 'upi' }
      selectedPaymentMode.value = modes[modeKey]
      // Re-focus amount input after mode switch
      setTimeout(() => {
        const input = amountInputRef.value?.$el?.querySelector('input') || amountInputRef.value
        input?.focus()
        input?.select()
      }, 50)
    }

    // Quick-cash denomination keys (1-6) when on payment screen
    if (!isInput && selectedPaymentMode.value === 'cash' && e.key >= '1' && e.key <= '6') {
      e.preventDefault()
      const idx = parseInt(e.key) - 1
      if (idx < quickCashDenominations.value.length) {
        amountReceived.value = quickCashDenominations.value[idx].amount
      }
    }
  }
}

// Show stock limit warning
const warnStockLimit = (item) => {
  const name = item?.product_name || 'Item'
  const stock = item?.stock_quantity ?? 0
  toast.warning(`Only ${stock} ${name} in stock`, { duration: 3000 })
}

// Wrapper for increment that shows stock warning
const handleIncrement = (index) => {
  const result = incrementQuantity(index)
  if (result?.capped) warnStockLimit(cartItems.value[index])
}

// Handle inline qty input change (direct typing in qty field)
const handleQtyInputChange = (index, event) => {
  const newQty = parseFloat(event.target.value) || 0
  if (newQty > 0) {
    const result = updateQuantity(index, newQty)
    if (result?.capped) warnStockLimit(cartItems.value[index])
  } else if (newQty <= 0) {
    removeItem(index)
  }
}

// Handle inline price input change
const handlePriceInputChange = (index, event) => {
  const newPrice = parseFloat(event.target.value) || 0
  updateUnitPrice(index, newPrice)
}

// Handle inline item discount change
const handleItemDiscountChange = (index, event) => {
  const value = parseFloat(event.target.value) || 0
  const type = cartItems.value[index].discount_type || 'percentage'
  applyItemDiscount(index, type, value)
}

// Toggle item discount type between % and ₹
const toggleItemDiscountType = (index) => {
  const item = cartItems.value[index]
  const newType = item.discount_type === 'percentage' ? 'fixed' : 'percentage'
  // Re-apply with new type, keeping the same value
  applyItemDiscount(index, newType, item.discount_value || 0)
}

// Commit F9 qty edit
const commitQtyEdit = () => {
  if (editingQtyIndex.value >= 0) {
    const newQty = parseFloat(editingQtyValue.value) || 0
    if (newQty > 0) {
      const result = updateQuantity(editingQtyIndex.value, newQty)
      if (result?.capped) warnStockLimit(cartItems.value[editingQtyIndex.value])
    } else {
      removeItem(editingQtyIndex.value)
    }
    editingQtyIndex.value = -1
    editingQtyValue.value = ''
    // Return focus to search for next item
    nextTick(() => searchInputRef.value?.focus())
  }
}

// Commit price edit (inline price override)
const commitPriceEdit = () => {
  if (editingPriceIndex.value >= 0) {
    const newPrice = parseFloat(editingPriceValue.value) || 0
    if (newPrice > 0) {
      updateUnitPrice(editingPriceIndex.value, newPrice)
    }
    editingPriceIndex.value = -1
    editingPriceValue.value = ''
    nextTick(() => searchInputRef.value?.focus())
  }
}

// Get qty step based on unit type (decimal for weight/volume, integer for pieces)
// Uses same unit codes as InvoiceForm (HSN standard: KGS, LTR, MTR, etc.)
const CONTINUOUS_UNITS = new Set([
  'KGS', 'GMS', 'LTR', 'MLT', 'MTR', 'CMS', 'KME', 'KLR',
  'MTS', 'TON', 'QTL', 'CBM', 'CCM', 'SQM', 'SQF', 'SQY',
  'YDS', 'GYD', 'UGS',
])
const getQtyStep = (unitType) => {
  if (!unitType) return 1
  return CONTINUOUS_UNITS.has(unitType) ? 0.01 : 1
}

// Format tax rate for display
const formatTaxRate = (item) => {
  if (!item.is_taxable) return ''
  const rate = (item.cgst_rate || 0) + (item.sgst_rate || 0) + (item.igst_rate || 0)
  return rate > 0 ? `${rate}%` : ''
}

// Calculate line total (qty * price - discount)
const lineTotal = (item) => {
  return (item.quantity * item.unit_price) - (item.discount_amount || 0)
}

// Auto-open qty edit input for the most recently added/selected cart item
const openQtyEditForSelectedItem = () => {
  const itemIndex = selectedCartItemIndex.value
  if (itemIndex < 0) return
  cartItemSubFocus.value = 0
  focusCartItemField(itemIndex, 0)
}

// Focus hidden barcode input (for hardware barcode scanners)
const focusBarcodeInput = (e) => {
  // Don't steal focus when print modal is shown or from interactive elements
  if (showPrintModal.value) return
  if (e?.target?.closest('.pos-search-bar, .pos-customer-bar, .pos-new-customer-inline, .pos-modal, .pos-payment-panel, .pos-print-overlay, input, button')) {
    return
  }
  barcodeInputRef.value?.focus()
}

// Timer for clock
let timeInterval = null

onMounted(() => {
  fetchBusinessName()
  updateTime()
  timeInterval = setInterval(updateTime, 1000)
  fetchData()
  focusBarcodeInput()

  // Keep focus on barcode input
  document.addEventListener('click', focusBarcodeInput)
})

onUnmounted(() => {
  if (timeInterval) clearInterval(timeInterval)
  document.removeEventListener('click', focusBarcodeInput)
})

// Auto-focus discount input when modal opens
// Auto-focus "New Sale" button when print modal opens
watch(showPrintModal, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      newSaleBtnRef.value?.focus()
    })
  }
})

watch(showDiscountModal, (isOpen) => {
  if (isOpen) {
    discountFormValue.value = 0
    discountFormType.value = 'percentage'
    discountTab.value = selectedCartItemIndex.value >= 0 ? 'item' : 'invoice'
    nextTick(() => discountInputRef.value?.focus())
  } else {
    restoreFocus()
  }
})

// Reset held highlight when modal opens
watch(showHeldModal, (isOpen) => {
  if (isOpen) {
    heldHighlightIndex.value = 0
  } else {
    restoreFocus()
  }
})

// Restore focus when help modal closes
watch(showHelpModal, (isOpen) => {
  if (!isOpen) restoreFocus()
})

// Watch for payment panel open to set amount
watch(isPaymentPanelOpen, (isOpen) => {
  if (isOpen) {
    // Non-cash: pre-fill exact amount (no change calculation needed)
    // Cash: leave 0 so cashier enters amount received for change calculation
    amountReceived.value = selectedPaymentMode.value === 'cash' ? 0 : grandTotal.value
    // Focus amount input after Transition renders (needs slight delay)
    setTimeout(() => {
      const input = amountInputRef.value?.$el?.querySelector('input') || amountInputRef.value
      input?.focus()
      input?.select()
    }, 100)
  }
})

// Watch for payment mode change to update amount field
watch(selectedPaymentMode, (newMode) => {
  if (isPaymentPanelOpen.value) {
    // Update amount when switching payment modes
    amountReceived.value = newMode === 'cash' ? 0 : grandTotal.value
  }
})
</script>

<style scoped>
/* ==================== CSS VARIABLES ==================== */
/*
 * POS now uses Intercom design tokens (light theme)
 * All styling uses standard Intercom CSS variables:
 * - --color-white → white background
 * - --color-charcoal → dark text
 * - --color-blue → accent color
 * - See client/shared/styles/intercom-tokens.css for full token list
 */

/* ==================== LAYOUT ==================== */
.pos-view {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--color-white);
  color: var(--color-charcoal);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow: hidden;
}

/* Hidden barcode input */
.pos-barcode-input {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

/* ==================== HEADER ==================== */
.pos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: var(--color-gray-50);
  border-bottom: 1px solid var(--color-gray-200);
}

.pos-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pos-logo {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.pos-logo-text {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-blue);
}

.pos-logo-product {
  font-size: 14px;
  color: var(--color-gray-500);
}

.pos-header-center {
  font-size: 18px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.pos-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pos-user {
  color: var(--color-gray-500);
}

.pos-header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-gray-500);
  cursor: pointer;
  transition: all 0.2s;
}

.pos-header-btn:hover {
  background: var(--color-gray-100);
  color: var(--color-charcoal);
}

/* ==================== MAIN LAYOUT ==================== */
.pos-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Cart Panel — full width by default, shrinks when payment panel opens */
.pos-cart-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  border-right: 1px solid var(--color-gray-200);
}

/* Customer Bar with Dropdown */
/* Customer Bar: HSN-style unified search-or-create */
.pos-customer-bar {
  padding: 8px 16px;
  background: var(--color-gray-50);
  border-bottom: 1px solid var(--color-gray-200);
}

.pos-customer-search-wrapper {
  position: relative;
}

.pos-customer-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-gray-400);
  pointer-events: none;
  z-index: 1;
}

.pos-customer-search-input {
  width: 100%;
  height: 38px;
  padding: 0 12px 0 34px;
  font-size: 14px;
  color: var(--color-charcoal);
  background: var(--color-gray-100);
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  outline: none;
  transition: all 0.15s;
}

.pos-customer-search-input:focus {
  background: white;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

/* Selected customer badge (overlays input when customer is chosen) */
.pos-customer-badge {
  position: absolute;
  inset: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px 0 34px;
  background: var(--color-gray-50);
  border-radius: 6px;
  cursor: text;
}

.pos-customer-badge-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-charcoal);
}

.pos-customer-badge-phone {
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-customer-badge-pts {
  padding: 1px 6px;
  background: var(--color-blue);
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.pos-customer-badge-clear {
  margin-left: auto;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--color-gray-400);
  background: var(--color-gray-200);
  border: none;
  border-radius: 50%;
  cursor: pointer;
}

.pos-customer-badge-clear:hover {
  background: var(--color-gray-300);
  color: var(--color-charcoal);
}

/* Customer search dropdown */
.pos-customer-dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  max-height: 220px;
  overflow-y: auto;
  z-index: 100;
}

.pos-customer-dropdown-loading {
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: var(--color-gray-400);
}

.pos-customer-dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
}

.pos-customer-dropdown-item:hover {
  background: var(--color-gray-50);
}

.pos-customer-dropdown-item.highlighted {
  background: var(--color-indigo-50, #eef2ff);
  border-left: 3px solid var(--color-indigo-500, #6366f1);
  padding-left: 9px;
}

.pos-customer-item-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-charcoal);
}

.pos-customer-item-phone {
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-customer-create-option {
  border-top: 1px solid var(--color-gray-100);
  color: var(--color-blue);
  font-weight: 500;
  font-size: 14px;
}

/* Inline create: compact phone-only bar */
.pos-new-customer-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--color-blue-50, #eff6ff);
  border-bottom: 1px solid var(--color-blue-200, #bfdbfe);
}

.pos-new-customer-inline-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-blue, #3b82f6);
  white-space: nowrap;
}

.pos-new-customer-inline-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.pos-new-customer-inline-input {
  flex: 1;
  height: 32px;
  padding: 0 8px;
  font-size: 14px;
  border: 1px solid var(--color-blue-300, #93c5fd);
  border-radius: 6px;
  background: white;
  color: var(--color-charcoal);
  outline: none;
}

.pos-new-customer-inline-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(0, 87, 255, 0.1);
}

.pos-new-customer-inline-cancel {
  padding: 4px 8px;
  font-size: 12px;
  color: var(--color-gray-500);
  background: none;
  border: none;
  cursor: pointer;
}

.pos-new-customer-inline-cancel:hover {
  color: var(--color-charcoal);
}

/* Cart Items */
.pos-cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.pos-cart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-gray-500);
  text-align: center;
}

.pos-cart-empty p {
  margin: 16px 0 4px;
  font-size: 18px;
}

.pos-cart-empty span {
  font-size: 14px;
  opacity: 0.7;
}

/* Cart header + item share the same column grid */
.pos-cart-header,
.pos-cart-item {
  display: grid;
  grid-template-columns: 1fr 110px 72px 80px 44px 72px 28px;
  gap: 6px;
  align-items: center;
  padding: 0 8px;
}

.pos-cart-header {
  padding-top: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--color-gray-200);
  position: sticky;
  top: 0;
  background: var(--color-white);
  z-index: 1;
}

.pos-col {
  min-width: 0;
}

.pos-cart-header .pos-col {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-gray-400);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pos-cart-item {
  padding-top: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-gray-100);
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
}

.pos-cart-item:hover {
  background: var(--color-gray-50);
}

.pos-cart-item.selected {
  border-left-color: var(--color-blue);
  background: var(--color-blue-light, #f0f4ff);
}

/* Product column */
.pos-col-name {
  overflow: hidden;
}

.pos-cart-item-name {
  display: block;
  font-weight: 500;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pos-cart-item-sku {
  display: block;
  font-size: 11px;
  color: var(--color-gray-400);
}

/* Qty column */
.pos-col-qty {
  text-align: center;
}

.pos-qty-group {
  display: flex;
  align-items: center;
  gap: 2px;
}

.pos-qty-btn {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: var(--color-gray-200);
  color: var(--color-charcoal);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
  line-height: 1;
}

.pos-qty-btn:hover {
  background: var(--color-blue);
  color: white;
}

.pos-unit-label {
  font-size: 10px;
  color: var(--color-gray-400);
  text-align: center;
  display: block;
  margin-top: 1px;
}

/* Shared input style for qty/price/disc */
.pos-field-input {
  width: 100%;
  padding: 3px 5px;
  border: 1px solid var(--color-gray-200);
  border-radius: 4px;
  font-size: 13px;
  background: var(--color-white);
  font-variant-numeric: tabular-nums;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.pos-field-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.15);
}

/* Remove number input spinners */
.pos-field-input::-webkit-outer-spin-button,
.pos-field-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.pos-field-input[type=number] {
  -moz-appearance: textfield;
}

.pos-field-input--qty {
  width: 44px;
  text-align: center;
  font-weight: 600;
}

/* Price column */
.pos-col-price .pos-field-input {
  text-align: right;
}

/* Discount column */
.pos-disc-group {
  display: flex;
  align-items: center;
}

.pos-field-input--disc {
  border-radius: 4px 0 0 4px;
  border-right: none;
  text-align: right;
}

.pos-disc-toggle {
  padding: 3px 5px;
  border: 1px solid var(--color-gray-200);
  border-radius: 0 4px 4px 0;
  background: var(--color-gray-100);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: var(--color-gray-600);
  transition: all 0.15s;
  line-height: 1;
  height: 26px;
}

.pos-disc-toggle:hover {
  background: var(--color-blue);
  color: white;
  border-color: var(--color-blue);
}

/* Tax column (read-only) */
.pos-col-tax {
  text-align: center;
}

.pos-tax-display {
  font-size: 12px;
  color: var(--color-gray-500);
  font-variant-numeric: tabular-nums;
}

/* Total column */
.pos-col-total {
  text-align: right;
}

.pos-total-display {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
}

/* Delete button */
.pos-col-del {
  text-align: center;
}

.pos-cart-item-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-gray-400);
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
}

.pos-cart-item:hover .pos-cart-item-remove {
  opacity: 1;
}

.pos-cart-item-remove:hover {
  background: #ef4444;
  color: white;
}

/* Cart Totals */
.pos-cart-totals {
  padding: 16px;
  background: var(--color-gray-50);
  border-top: 1px solid var(--color-gray-200);
}

.pos-total-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-variant-numeric: tabular-nums;
}

.pos-total-tax {
  font-size: 14px;
  color: var(--color-gray-500);
}

.pos-total-discount {
  color: var(--color-success);
}

.pos-total-grand {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 2px solid var(--color-gray-200);
  font-size: 24px;
  font-weight: 700;
}

/* ==================== PRODUCT PANEL ==================== */
/* Right Panel Container (for split view) */
/* Right Panel — only visible when payment panel is open */
.pos-right-panel {
  display: flex;
  flex-direction: column;
  width: 45%;
  position: relative;
  overflow: hidden;
}

.pos-right-panel:empty,
.pos-right-panel:not(:has(.pos-payment-panel)) {
  display: none;
  width: 0;
}

.pos-product-panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-gray-50);
}

/* Search Bar */
.pos-search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--color-gray-100);
  border-bottom: 1px solid var(--color-gray-200);
  position: relative;
}

/* Search Dropdown */
.pos-search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 400px;
  overflow-y: auto;
  z-index: 50;
}

.pos-search-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.1s;
  border-bottom: 1px solid var(--color-gray-50);
}

.pos-search-dropdown-item:last-child {
  border-bottom: none;
}

.pos-search-dropdown-item:hover,
.pos-search-dropdown-item.highlighted {
  background: var(--color-blue-light, #eef4ff);
}

.pos-search-dropdown-item.highlighted {
  border-left: 3px solid var(--color-blue);
  padding-left: 13px;
}

.pos-search-dropdown-item.out-of-stock {
  opacity: 0.4;
  cursor: not-allowed;
}

.pos-search-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pos-search-item-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--color-charcoal);
}

.pos-search-item-sku {
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-search-item-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.pos-search-item-price {
  font-weight: 700;
  font-size: 14px;
  color: var(--color-blue);
}

.pos-search-item-stock {
  font-size: 11px;
  color: var(--color-gray-500);
}

.pos-search-item-stock.low {
  color: var(--color-red, #ef4444);
}

.pos-search-dropdown-empty {
  padding: 20px 16px;
  text-align: center;
  color: var(--color-gray-500);
  font-size: 14px;
}

.pos-search-input {
  flex: 1;
  padding: 10px 0;
  background: transparent;
  border: none;
  color: var(--color-charcoal);
  font-size: 16px;
  outline: none;
}

.pos-search-input::placeholder {
  color: var(--color-gray-500);
}

.pos-search-clear,
.pos-camera-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: var(--color-gray-50);
  color: var(--color-gray-500);
  cursor: pointer;
  transition: all 0.2s;
}

.pos-search-clear:hover,
.pos-camera-btn:hover {
  background: var(--color-blue);
  color: var(--color-charcoal);
}

.pos-loading,
.pos-no-products {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--color-gray-500);
}

.pos-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-gray-200);
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ==================== ACTION BAR ==================== */
.pos-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--color-gray-50);
  border-top: 1px solid var(--color-gray-200);
}

.pos-action-left,
.pos-action-right {
  display: flex;
  gap: 8px;
}

.pos-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: var(--color-gray-100);
  color: var(--color-charcoal);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pos-action-btn:hover:not(:disabled) {
  background: var(--color-gray-50);
}

.pos-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pos-action-key {
  padding: 2px 6px;
  background: var(--color-gray-50);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.pos-action-held {
  background: var(--color-warning);
  color: #000;
}

.pos-action-confirm {
  background: var(--color-red, #ef4444);
  color: #fff;
  animation: pulse-confirm 0.6s ease-in-out infinite alternate;
}

@keyframes pulse-confirm {
  from { opacity: 1; }
  to { opacity: 0.7; }
}

.pos-action-pay {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  font-size: 16px;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pos-action-pay:hover:not(:disabled) {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
}

.pos-action-pay:focus-visible {
  outline: none;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3),
              0 0 0 3px rgba(34, 197, 94, 0.5);
}

.pos-action-pay:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.pos-action-pay:disabled {
  background: var(--color-gray-100);
  color: var(--color-gray-500);
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.6;
}

/* ==================== PAYMENT PANEL (Split View) ==================== */
.pos-payment-panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-gray-50);
  padding: 24px;
  overflow-y: auto;
}

.pos-payment-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-gray-200);
}

.pos-back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: var(--color-gray-100);
  color: var(--color-charcoal);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pos-back-btn:hover {
  background: var(--color-gray-50);
}

.pos-payment-header h2 {
  flex: 1;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.pos-payment-phone {
  font-size: 14px;
  color: var(--color-gray-500);
}

.pos-payment-amount {
  text-align: center;
  margin-bottom: 32px;
}

.pos-payment-label {
  display: block;
  font-size: 14px;
  color: var(--color-gray-500);
  margin-bottom: 8px;
}

.pos-payment-value {
  font-size: 48px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pos-payment-modes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.pos-payment-mode {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 16px;
  border: 2px solid var(--color-gray-200);
  border-radius: 12px;
  background: transparent;
  color: var(--color-charcoal);
  cursor: pointer;
  transition: all 0.2s;
}

.pos-payment-mode:hover {
  border-color: var(--color-blue);
}

.pos-payment-mode.active {
  border-color: var(--color-blue);
  background: var(--color-blue-light);
}

.pos-payment-mode-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pos-payment-mode-icon svg {
  width: 24px;
  height: 24px;
  stroke: var(--color-gray-500);
  transition: stroke 0.2s;
}

.pos-payment-mode:hover .pos-payment-mode-icon svg,
.pos-payment-mode.active .pos-payment-mode-icon svg {
  stroke: var(--color-blue);
}

.pos-payment-mode-label {
  font-weight: 500;
}

.pos-payment-mode-key {
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-payment-input,
.pos-payment-reference,
.pos-payment-loyalty {
  margin-bottom: 16px;
}

.pos-payment-input label,
.pos-payment-reference label,
.pos-payment-loyalty label {
  display: block;
  font-size: 14px;
  color: var(--color-gray-500);
  margin-bottom: 8px;
}

.pos-payment-input input,
.pos-payment-reference input,
.pos-payment-loyalty input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  background: var(--color-gray-50);
  color: var(--color-charcoal);
  font-size: 18px;
  font-variant-numeric: tabular-nums;
}

.pos-payment-input input:focus,
.pos-payment-reference input:focus {
  border-color: var(--color-blue);
  outline: none;
}

.pos-loyalty-input {
  display: flex;
  gap: 8px;
  align-items: center;
}

.pos-loyalty-input input {
  flex: 1;
}

.pos-loyalty-value {
  color: var(--color-success);
  font-weight: 500;
}

.pos-loyalty-info {
  margin-top: 6px;
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-payment-change {
  display: flex;
  justify-content: space-between;
  padding: 16px;
  background: var(--color-gray-100);
  border-radius: 8px;
  margin-bottom: 24px;
}

.pos-payment-change-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-success);
}

/* GST Toggle */
.pos-gst-toggle {
  padding: 12px 16px;
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  margin-bottom: 16px;
}

.pos-gst-hint {
  display: block;
  font-size: 12px;
  color: var(--color-warning);
  margin-top: 8px;
}

/* Payment Field wrapper for IntercomInput */
.pos-payment-field {
  margin-bottom: 16px;
}

/* Override IntercomButton for green "Complete Sale" button */
.pos-complete-btn.intercom-btn {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  font-size: 18px;
}

.pos-complete-btn.intercom-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
}

.pos-complete-btn.intercom-btn:disabled {
  background: var(--color-gray-200);
  color: var(--color-gray-400);
}

.pos-confirm-pulse.intercom-btn {
  animation: confirm-pulse 1s ease-in-out infinite;
}

@keyframes confirm-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

/* ==================== SALE TYPE TOGGLE ==================== */
.pos-sale-type {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.sale-type-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--color-gray-200);
  background: var(--color-white);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  color: var(--color-charcoal);
}

.sale-type-btn.active {
  background: var(--color-blue);
  color: white;
  border-color: var(--color-blue);
}

.sale-type-btn:hover:not(.active) {
  background: var(--color-gray-50);
  border-color: var(--color-gray-300);
}

.credit-warning {
  color: var(--color-error, #ef4444);
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
}

.pos-credit-balance {
  font-size: 12px;
  color: var(--color-warning, #f59e0b);
  margin-top: 4px;
  margin-bottom: 8px;
  font-weight: 500;
}

/* ==================== MODALS ==================== */
.pos-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.pos-modal {
  width: 480px;
  max-height: 80vh;
  background: var(--color-gray-50);
  border-radius: 16px;
  overflow: hidden;
}

.pos-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-gray-200);
}

.pos-modal-header h3 {
  font-size: 18px;
  font-weight: 600;
}

.pos-modal-header button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-gray-500);
  font-size: 24px;
  cursor: pointer;
}

.pos-modal-header button:hover {
  background: var(--color-gray-100);
}

.pos-modal-search {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-gray-200);
}

.pos-modal-search input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  background: var(--color-gray-50);
  color: var(--color-charcoal);
  font-size: 14px;
}

.pos-modal-list {
  max-height: 400px;
  overflow-y: auto;
}

.pos-modal-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.pos-modal-item:hover {
  background: var(--color-gray-100);
}

.pos-modal-item-walkin {
  background: var(--color-gray-100);
  border-bottom: 1px solid var(--color-gray-200);
}

.pos-modal-item-name {
  font-weight: 500;
}

.pos-modal-item-phone {
  margin-left: 12px;
  color: var(--color-gray-500);
  font-size: 14px;
}

.pos-modal-item-sub {
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-modal-item-points {
  padding: 4px 10px;
  background: var(--color-blue);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* Help Modal */
.pos-modal-help {
  width: 400px;
}

.pos-help-content {
  padding: 16px;
}

.pos-help-section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  padding-top: 12px;
  margin-bottom: 4px;
  border-top: 1px solid var(--color-gray-100);
}

.pos-help-section:first-child {
  border-top: none;
  padding-top: 0;
}

.pos-help-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}

.pos-help-row kbd {
  min-width: 50px;
  padding: 4px 8px;
  background: var(--color-gray-100);
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
  text-align: center;
}

/* Quick cash denomination buttons */
.pos-quick-cash {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.pos-quick-cash-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-charcoal);
  background: var(--color-gray-100);
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.pos-quick-cash-btn:hover {
  background: var(--color-gray-200);
}

.pos-quick-cash-btn.active {
  background: var(--color-indigo-100, #e0e7ff);
  border-color: var(--color-indigo-400, #818cf8);
  color: var(--color-indigo-700, #4338ca);
}

.pos-quick-cash-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  background: rgba(255,255,255,0.8);
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 3px;
}

/* Qty Multiplier Badge */
.pos-qty-multiplier-badge {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(79, 70, 229, 0.95);
  color: white;
  font-size: 48px;
  font-weight: 800;
  padding: 16px 32px;
  border-radius: 16px;
  z-index: 9999;
  pointer-events: none;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 8px 32px rgba(79, 70, 229, 0.4);
}

/* ==================== TRANSITIONS ==================== */
/* Slide transitions for split view panels */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1024px) {
  .pos-main {
    flex-direction: column;
  }

  .pos-cart-panel,
  .pos-right-panel {
    width: 100%;
  }

  .pos-cart-panel {
    height: 50%;
    border-right: none;
    border-bottom: 1px solid var(--color-gray-200);
  }

  .pos-right-panel {
    height: 50%;
  }
}

@media (max-width: 768px) {
  .pos-action-bar {
    flex-direction: column;
    gap: 8px;
  }

  .pos-action-left,
  .pos-action-right {
    width: 100%;
  }

  .pos-action-btn {
    flex: 1;
    justify-content: center;
  }

  .pos-payment-panel {
    width: 100%;
  }
}

/* ==================== NAVIGATION DRAWER ==================== */
.pos-nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
}

.pos-nav-drawer {
  width: 280px;
  height: 100%;
  background: var(--color-gray-50);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pos-nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: var(--color-gray-100);
}

.pos-nav-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-charcoal);
}

.pos-nav-close {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: var(--color-gray-500);
  border-radius: 8px;
  transition: all 0.2s;
}

.pos-nav-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-charcoal);
}

.pos-nav-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
}

.pos-nav-section {
  margin-bottom: 8px;
}

.pos-nav-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-gray-500);
  padding: 12px 20px 8px;
}

.pos-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: var(--color-charcoal);
  text-decoration: none;
  transition: all 0.2s;
  border-left: 3px solid transparent;
}

.pos-nav-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-left-color: var(--color-blue);
}

.pos-nav-item.router-link-exact-active {
  background: var(--color-blue-light);
  border-left-color: var(--color-blue);
  color: var(--color-blue);
}

.pos-nav-item svg {
  flex-shrink: 0;
}

.pos-nav-item span {
  font-size: 14px;
}

/* Drawer transitions */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-enter-active .pos-nav-drawer,
.drawer-leave-active .pos-nav-drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .pos-nav-drawer,
.drawer-leave-to .pos-nav-drawer {
  transform: translateX(-100%);
}

/* ==================== PRINT RECEIPT MODAL ==================== */
.pos-print-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--color-gray-100);
  display: flex;
  flex-direction: column;
}

.pos-print-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: var(--color-white);
  border-bottom: 1px solid var(--color-gray-200);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.pos-print-sale-done {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-success, #16a34a);
}

.pos-print-sale-done svg {
  stroke: var(--color-success, #16a34a);
}

.pos-print-actions {
  display: flex;
  gap: 12px;
}

.pos-print-new-sale-btn {
  padding: 10px 20px;
  background: var(--color-white);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 500;
  color: var(--color-gray-600);
  cursor: pointer;
  transition: all 0.2s;
}

.pos-print-new-sale-btn:hover {
  background: var(--color-gray-50);
  border-color: var(--color-gray-400);
}

.pos-print-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--color-blue);
  border: none;
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.pos-print-btn:hover {
  opacity: 0.9;
}

.pos-print-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  justify-content: center;
}

/* A5 preview — constrain to A5 proportions on screen */
.pos-print-content.preview-a5 {
  align-items: flex-start;
}

.pos-print-content.preview-a5 > * {
  width: 148mm;
  min-height: 210mm;
  max-width: 148mm;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
  background: white;
}

/* ==================== KEYBOARD FOCUS INDICATORS ==================== */
/* Only show focus rings for keyboard users (not mouse clicks) */
.pos-view :focus-visible {
  outline: 2px solid var(--color-blue);
  outline-offset: 2px;
  border-radius: 4px;
}

.pos-action-btn:focus-visible {
  outline: 2px solid var(--color-blue);
  outline-offset: 2px;
}

.pos-payment-mode:focus-visible {
  outline: 2px solid var(--color-blue);
  outline-offset: 2px;
}

/* Action bar roving tabindex — show focus ring on the active button */
.pos-action-bar button:focus-visible {
  outline: 2px solid var(--color-blue);
  outline-offset: 2px;
  background: var(--color-gray-100);
}

/* Cart item sub-button focus (-, +, delete) via ArrowLeft/Right */
.pos-qty-btn:focus-visible,
.pos-cart-item-remove:focus-visible {
  outline: 2px solid var(--color-blue);
  outline-offset: 2px;
  opacity: 1;
}

/* ==================== DISCOUNT MODAL ==================== */
.pos-modal-discount {
  max-width: 400px;
  width: 90%;
}

.pos-discount-content {
  padding: 16px;
}

.pos-discount-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.pos-discount-tab {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--color-gray-300);
  border-radius: 6px;
  background: var(--color-white);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pos-discount-tab.active {
  border-color: var(--color-blue);
  background: var(--color-blue-light, #eef4ff);
  color: var(--color-blue);
  font-weight: 600;
}

.pos-discount-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pos-discount-type-row {
  display: flex;
  gap: 8px;
}

.pos-discount-type-btn {
  flex: 1;
  padding: 10px;
  border: 1px solid var(--color-gray-300);
  border-radius: 6px;
  background: var(--color-white);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.pos-discount-type-btn.active {
  border-color: var(--color-blue);
  background: var(--color-blue-light, #eef4ff);
  color: var(--color-blue);
}

.pos-discount-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  outline: none;
  box-sizing: border-box;
}

.pos-discount-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.pos-discount-quick-btns {
  display: flex;
  gap: 8px;
}

.pos-discount-quick {
  flex: 1;
  padding: 8px;
  border: 1px solid var(--color-gray-200);
  border-radius: 6px;
  background: var(--color-gray-50);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.pos-discount-quick:hover {
  border-color: var(--color-blue);
  background: var(--color-blue-light, #eef4ff);
}

/* ==================== HELD TRANSACTIONS MODAL ==================== */
.pos-modal-held {
  max-width: 450px;
  width: 90%;
}

.pos-held-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px 0;
}

.pos-held-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-left: 3px solid transparent;
}

.pos-held-item:hover,
.pos-held-item.highlighted {
  background: var(--color-gray-50);
  border-left-color: var(--color-blue);
}

.pos-held-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pos-held-item-customer {
  font-weight: 600;
  font-size: 14px;
}

.pos-held-item-time {
  font-size: 12px;
  color: var(--color-gray-500);
}

.pos-held-item-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 13px;
  color: var(--color-gray-600);
}

.pos-held-item-total {
  font-weight: 600;
  color: var(--color-charcoal);
}

.pos-held-item-delete {
  padding: 4px;
  border: none;
  background: none;
  color: var(--color-gray-400);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s;
}

.pos-held-item-delete:hover {
  color: var(--color-red, #ef4444);
  background: rgba(239, 68, 68, 0.08);
}

.pos-held-empty {
  padding: 32px 16px;
  text-align: center;
  color: var(--color-gray-500);
  font-size: 14px;
}

.pos-held-hint {
  padding: 8px 16px;
  border-top: 1px solid var(--color-gray-100);
  font-size: 11px;
  color: var(--color-gray-400);
  text-align: center;
}

.pos-held-hint kbd {
  display: inline-block;
  padding: 1px 5px;
  border: 1px solid var(--color-gray-300);
  border-radius: 3px;
  background: var(--color-gray-50);
  font-size: 10px;
  font-family: inherit;
}


</style>

<!-- Unscoped: @media print rules for Teleported print overlay -->
<style>
@media print {
  /* Hide the app — the print overlay is outside #app via Teleport */
  #app {
    display: none !important;
  }

  .pos-print-overlay {
    position: static !important;
    inset: auto !important;
    z-index: auto !important;
    background: white !important;
    overflow: visible !important;
    height: auto !important;
    padding: 0 !important;
    display: block !important;
  }

  .pos-print-toolbar {
    display: none !important;
  }

  .pos-print-content {
    padding: 0 !important;
    overflow: visible !important;
    max-height: none !important;
    height: auto !important;
    display: block !important;
  }

  .pos-print-content > * {
    width: 100% !important;
    max-width: none !important;
    box-shadow: none !important;
    margin: 0 !important;
  }

  @page {
    margin: 6mm;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
